// src/pages/AdminPage.jsx
// Admin Management - user CRUD + system information

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import AppShell from "../components/AppShell";
import UserActionsBar from "../components/UserActionsBar";
import Toast from "../components/Toast";
import ReportLayoutSection from "../components/ReportLayoutSection";

import {
  adminListUsers,
  adminCreateUser,
  adminUpdateUser,
  adminDeleteUser,
  adminGetSystemInfo,
  getQueueHealth,
  fetchSearchApiUsage,
  copilotMetricList,
  copilotMetricCreate,
  copilotMetricUpdate,
  copilotMetricDelete,
  copilotMetricTypes,
  inviteUser,
  listInvitations,
  revokeInvitation,
  resendInvitation,
  fetchBrandHub,
  adminListDomainCategories,
  adminUpdateDomainCategory,
  adminCreateDomainCategory,
  adminDeleteDomainCategory,
  listReleases,
  createRelease,
  updateRelease,
  deleteRelease,
  getReportLayout,
  putReportLayout,
} from "../services/api";

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function UserRow({ user, currentUserId, onSave, onDelete, onResetPw, onReset2fa, brands }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    full_name: user.full_name || "",
    is_admin: user.is_admin,
    is_active: user.is_active,
    role: user.role || "analyst",
    llm_access: user.llm_access !== false,
    ads_access: user.ads_access !== false,
    monthly_cost_limit_usd: user.monthly_cost_limit_usd ?? 20,
    brand_ids: user.brand_ids || [],
  });
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showPwReset, setShowPwReset] = useState(false);
  const [newPw, setNewPw] = useState("");

  const isSelf = user.id === currentUserId;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(user.id, form);
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleResetPw = async () => {
    if (!newPw || newPw.length < 6) return;
    setSaving(true);
    try {
      await onResetPw(user.id, newPw);
      setShowPwReset(false);
      setNewPw("");
    } finally {
      setSaving(false);
    }
  };

  const cell = { padding: "10px 12px", fontSize: 13, verticalAlign: "middle" };
  const inputStyle = {
    border: "1px solid #d1d5db",
    borderRadius: 6,
    padding: "4px 8px",
    fontSize: 13,
    width: "100%",
    boxSizing: "border-box",
  };

  return (
    <>
      <tr style={{ borderTop: "1px solid var(--border)" }}>
        <td style={{ ...cell, fontWeight: 500 }}>{user.id}</td>
        <td style={{ ...cell, fontWeight: 500 }}>
          {user.email}
          {isSelf && (
            <span
              style={{
                marginLeft: 8,
                fontSize: 10,
                background: "#dbeafe",
                color: "#1d4ed8",
                padding: "2px 6px",
                borderRadius: 4,
                fontWeight: 600,
              }}
            >
              YOU
            </span>
          )}
        </td>
        <td style={cell}>
          {editing ? (
            <input
              style={inputStyle}
              value={form.full_name}
              onChange={(e) => set("full_name", e.target.value)}
              placeholder="Full name"
            />
          ) : (
            user.full_name || <span style={{ color: "#9ca3af" }}>-</span>
          )}
        </td>
        <td style={{ ...cell, textAlign: "center" }}>
          {editing ? (
            <select
              style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "2px 6px", fontSize: 12 }}
              value={form.role}
              onChange={(e) => set("role", e.target.value)}
              disabled={isSelf}
            >
              <option value="admin">Admin</option>
              <option value="analyst">Analyst</option>
              <option value="viewer">Viewer</option>
            </select>
          ) : (
            <span style={{
              fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 4,
              background: user.role === "admin" ? "#dcfce7" : user.role === "viewer" ? "#fef3c7" : "#f3f4f6",
              color: user.role === "admin" ? "#166534" : user.role === "viewer" ? "#92400e" : "#374151",
            }}>
              {(user.role || "analyst").charAt(0).toUpperCase() + (user.role || "analyst").slice(1)}
            </span>
          )}
        </td>
        <td style={{ ...cell, textAlign: "center", fontSize: 11 }}>
          {editing ? (
            <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
              <label style={{ display: "flex", alignItems: "center", gap: 2, cursor: "pointer" }}>
                <input type="checkbox" checked={form.llm_access} onChange={(e) => set("llm_access", e.target.checked)} /> LLM
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 2, cursor: "pointer" }}>
                <input type="checkbox" checked={form.ads_access} onChange={(e) => set("ads_access", e.target.checked)} /> ADS
              </label>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
              {user.llm_access !== false && <span style={{ background: "#dbeafe", color: "#1d4ed8", padding: "1px 6px", borderRadius: 4, fontWeight: 600 }}>LLM</span>}
              {user.ads_access !== false && <span style={{ background: "#fce7f3", color: "#9d174d", padding: "1px 6px", borderRadius: 4, fontWeight: 600 }}>ADS</span>}
              {user.llm_access === false && user.ads_access === false && <span style={{ color: "#9ca3af" }}>None</span>}
            </div>
          )}
        </td>
        <td style={{ ...cell, textAlign: "center" }}>
          {editing ? (
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => set("is_active", e.target.checked)}
              disabled={isSelf}
            />
          ) : user.is_active ? (
            <span style={{ color: "#059669", fontWeight: 600 }}>Active</span>
          ) : (
            <span style={{ color: "#ef4444", fontWeight: 600 }}>Inactive</span>
          )}
        </td>
        <td style={{ ...cell, fontSize: 12, color: "#6b7280" }}>
          {user.created_at
            ? new Date(user.created_at).toLocaleDateString()
            : "-"}
        </td>
        <td style={{ ...cell, whiteSpace: "nowrap" }}>
          {editing ? (
            <>
              <button
                className="btn-primary"
                style={{ padding: "4px 12px", fontSize: 12, marginRight: 6 }}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save"}
              </button>
              <button
                className="btn-secondary"
                style={{ padding: "4px 10px", fontSize: 12 }}
                onClick={() => {
                  setEditing(false);
                  setForm({
                    full_name: user.full_name || "",
                    is_admin: user.is_admin,
                    is_active: user.is_active,
                    role: user.role || "analyst",
                    llm_access: user.llm_access !== false,
                    ads_access: user.ads_access !== false,
                    monthly_cost_limit_usd: user.monthly_cost_limit_usd ?? 20,
                    brand_ids: user.brand_ids || [],
                  });
                }}
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                className="btn-secondary"
                style={{ padding: "4px 10px", fontSize: 12, marginRight: 6 }}
                onClick={() => setEditing(true)}
              >
                Edit
              </button>
              <button
                className="btn-secondary"
                style={{ padding: "4px 10px", fontSize: 12, marginRight: 6 }}
                onClick={() => setShowPwReset(!showPwReset)}
              >
                Reset Pw
              </button>
              {user.totp_enabled && (
                <button
                  className="btn-secondary"
                  style={{ padding: "4px 10px", fontSize: 12, marginRight: 6, color: "#d97706", borderColor: "#fbbf24" }}
                  onClick={() => { if (window.confirm(`Reset 2FA for ${user.email}? They will need to set it up again.`)) onReset2fa(user.id); }}
                  title="Reset this user's two-factor authentication"
                >
                  Reset 2FA
                </button>
              )}
              {!isSelf &&
                (confirmDelete ? (
                  <>
                    <button
                      className="btn-danger"
                      style={{
                        padding: "4px 10px",
                        fontSize: 12,
                        marginRight: 4,
                      }}
                      onClick={() => onDelete(user.id)}
                    >
                      Confirm
                    </button>
                    <button
                      className="btn-secondary"
                      style={{ padding: "4px 8px", fontSize: 12 }}
                      onClick={() => setConfirmDelete(false)}
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <button
                    className="btn-secondary"
                    style={{
                      padding: "4px 10px",
                      fontSize: 12,
                      color: "#ef4444",
                      borderColor: "#fca5a5",
                    }}
                    onClick={() => setConfirmDelete(true)}
                  >
                    Delete
                  </button>
                ))}
            </>
          )}
        </td>
      </tr>
      {showPwReset && (
        <tr style={{ background: "#fafafa" }}>
          <td colSpan={2} />
          <td colSpan={5} style={{ padding: "8px 12px" }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input
                style={{ ...inputStyle, maxWidth: 220 }}
                type="password"
                placeholder="New password (min 6 chars)"
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
              />
              <button
                className="btn-primary"
                style={{ padding: "4px 12px", fontSize: 12 }}
                onClick={handleResetPw}
                disabled={saving || newPw.length < 6}
              >
                {saving ? "Resetting..." : "Set Password"}
              </button>
              <button
                className="btn-secondary"
                style={{ padding: "4px 10px", fontSize: 12 }}
                onClick={() => {
                  setShowPwReset(false);
                  setNewPw("");
                }}
              >
                Cancel
              </button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function CreateUserForm({ onCreated, brands }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    email: "",
    full_name: "",
    password: "",
    is_admin: false,
    role: "analyst",
    llm_access: true,
    ads_access: true,
    monthly_cost_limit_usd: 20,
    brand_ids: [],
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      await onCreated({ ...form, is_admin: form.role === "admin" });
      setForm({ email: "", full_name: "", password: "", is_admin: false, role: "analyst", llm_access: true, ads_access: true, monthly_cost_limit_usd: 20, brand_ids: [] });
      setOpen(false);
    } catch (err) {
      setError(err?.message || "Failed to create user");
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    border: "1px solid #d1d5db",
    borderRadius: 6,
    padding: "6px 10px",
    fontSize: 13,
    width: "100%",
    boxSizing: "border-box",
  };

  if (!open) {
    return (
      <button
        className="btn-primary"
        style={{ padding: "6px 14px", fontSize: 13 }}
        onClick={() => setOpen(true)}
      >
        + Add User
      </button>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        background: "#f9fafb",
        border: "1px solid var(--border)",
        borderRadius: 10,
        padding: 16,
        display: "flex",
        flexWrap: "wrap",
        gap: 10,
        alignItems: "flex-end",
      }}
    >
      <div style={{ flex: "1 1 180px" }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
          Email *
        </label>
        <input
          style={inputStyle}
          type="email"
          required
          value={form.email}
          onChange={(e) => set("email", e.target.value)}
          placeholder="user@example.com"
        />
      </div>
      <div style={{ flex: "1 1 160px" }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
          Full Name
        </label>
        <input
          style={inputStyle}
          value={form.full_name}
          onChange={(e) => set("full_name", e.target.value)}
          placeholder="John Doe"
        />
      </div>
      <div style={{ flex: "1 1 160px" }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
          Password *
        </label>
        <input
          style={inputStyle}
          type="password"
          required
          minLength={8}
          value={form.password}
          onChange={(e) => set("password", e.target.value)}
          placeholder="Min 8 chars, A-z, 0-9"
        />
      </div>
      <div style={{ flex: "1 1 120px" }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Role</label>
        <select style={{ ...inputStyle, height: 32 }} value={form.role} onChange={(e) => set("role", e.target.value)}>
          <option value="admin">Admin</option>
          <option value="analyst">Analyst</option>
          <option value="viewer">Viewer</option>
        </select>
      </div>
      <div style={{ flex: "0 0 auto", display: "flex", gap: 12, alignItems: "center" }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
          <input type="checkbox" checked={form.llm_access} onChange={(e) => set("llm_access", e.target.checked)} /> LLM
        </label>
        <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
          <input type="checkbox" checked={form.ads_access} onChange={(e) => set("ads_access", e.target.checked)} /> ADS
        </label>
      </div>
      <div style={{ flex: "1 1 100px" }}>
        <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Cost limit ($/mo)</label>
        <input style={inputStyle} type="number" step="1" min="0" value={form.monthly_cost_limit_usd} onChange={(e) => set("monthly_cost_limit_usd", parseFloat(e.target.value) || 0)} />
      </div>
      {form.role !== "admin" && brands && brands.length > 0 && (
        <div style={{ flex: "1 1 100%", marginTop: 4 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 4, display: "block" }}>Brand Access</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {brands.map((b) => (
              <label key={b.id} style={{ fontSize: 12, color: "#374151", display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={form.brand_ids.includes(b.id)}
                  onChange={(e) => {
                    if (e.target.checked) set("brand_ids", [...form.brand_ids, b.id]);
                    else set("brand_ids", form.brand_ids.filter((id) => id !== b.id));
                  }}
                />
                {b.name}
              </label>
            ))}
          </div>
        </div>
      )}
      <div style={{ flex: "0 0 auto", display: "flex", gap: 6 }}>
        <button
          className="btn-primary"
          style={{ padding: "6px 14px", fontSize: 13 }}
          type="submit"
          disabled={saving}
        >
          {saving ? "Creating..." : "Create"}
        </button>
        <button
          className="btn-secondary"
          style={{ padding: "6px 12px", fontSize: 13 }}
          type="button"
          onClick={() => setOpen(false)}
        >
          Cancel
        </button>
      </div>
      {error && (
        <div
          style={{
            width: "100%",
            fontSize: 12,
            color: "#ef4444",
            marginTop: 4,
          }}
        >
          {error}
        </div>
      )}
    </form>
  );
}

function QueueHealthCards({ queue }) {
  if (!queue) {
    return (
      <div style={{ color: "#9ca3af", fontSize: 13 }}>Loading...</div>
    );
  }

  const redisUp = queue.redis === "up";
  const redisColor = redisUp ? "#16a34a" : "#dc2626";
  const redisLabel = redisUp ? "Connected" : "Unreachable";

  let workerColor = "#dc2626";
  let workerLabel = "No worker";
  if (queue.worker_status === "live") {
    workerColor = "#16a34a";
    workerLabel = "Live";
  } else if (queue.worker_status === "stale") {
    workerColor = "#d97706";
    workerLabel = "Stale";
  }

  const seenAgo =
    typeof queue.worker_seen_seconds_ago === "number"
      ? `${queue.worker_seen_seconds_ago}s ago`
      : "never";

  const fallbackNote =
    !redisUp || queue.worker_status !== "live"
      ? "Background jobs are running INLINE - they will not survive a backend restart."
      : null;

  return (
    <>
      <div
        style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 8 }}
      >
        <InfoCard label="Redis" value={redisLabel} color={redisColor} />
        <InfoCard label="Worker" value={workerLabel} color={workerColor} />
        <InfoCard label="Last Heartbeat" value={seenAgo} />
        <InfoCard
          label="Pending Jobs"
          value={
            typeof queue.queued_jobs === "number" ? String(queue.queued_jobs) : "-"
          }
        />
      </div>
      {queue.redis_error && (
        <div
          style={{
            fontSize: 11,
            color: "#b91c1c",
            fontFamily: "monospace",
            marginBottom: 4,
          }}
        >
          {queue.redis_error}
        </div>
      )}
      {fallbackNote && (
        <div
          style={{
            fontSize: 12,
            color: "#92400e",
            background: "#fef3c7",
            border: "1px solid #fde68a",
            borderRadius: 6,
            padding: "6px 10px",
            marginBottom: 4,
          }}
        >
          {fallbackNote}
        </div>
      )}
    </>
  );
}

function InfoCard({ label, value, color }) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid var(--border)",
        borderRadius: 10,
        padding: "12px 16px",
        flex: "1 1 200px",
      }}
    >
      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 4 }}>
        {label}
      </div>
      <div
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: color || "#111827",
          fontFamily: "monospace",
          wordBreak: "break-all",
        }}
      >
        {value ?? "-"}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Copilot Metric components
// ---------------------------------------------------------------------------

function MetricsHelpTooltip() {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ marginBottom: 12, marginTop: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <p style={{ fontSize: 12, color: "#6b7280", margin: 0 }}>
          Template-based metrics that auto-compute exact numbers in copilot sessions.
        </p>
        <button
          onClick={() => setOpen((v) => !v)}
          style={{
            background: "none", border: "1px solid #d1d5db", borderRadius: 999,
            width: 20, height: 20, fontSize: 12, fontWeight: 800, color: "#6b7280",
            cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0,
          }}
          title="How custom metrics work"
        >
          ?
        </button>
      </div>
      {open && (
        <div style={{
          marginTop: 10, padding: 16, background: "#f0fdf4",
          border: "1px solid #bbf7d0", borderRadius: 10,
          fontSize: 13, color: "#374151", lineHeight: 1.7,
        }}>
          <div style={{ fontWeight: 800, marginBottom: 8, color: "#111827" }}>How Custom Metrics Work</div>
          <p style={{ margin: "0 0 10px" }}>
            Custom metrics let you define <strong>repeatable, exact measurements</strong> that run automatically
            every time a copilot session is created. Results appear as pre-computed tables in the copilot's context,
            so the LLM uses your exact numbers instead of counting manually.
          </p>

          <div style={{ fontWeight: 700, marginBottom: 6, color: "#111827" }}>Available Metric Types</div>
          <div style={{ display: "grid", gap: 6, marginBottom: 12 }}>
            <div><strong>Keyword Count</strong> - Count how many answers contain specific terms.
              <span style={{ color: "#6b7280" }}> Example: keywords "visit store, physical location" counts answers mentioning physical retail.</span></div>
            <div><strong>Co-Mention</strong> - Count answers where the brand and a competitor appear together.
              <span style={{ color: "#6b7280" }}> Example: brand "Zentiva" with competitors "Doppelherz, Solgar" shows co-occurrence per model.</span></div>
            <div><strong>Regex Match</strong> - Count matches for a regex pattern across answers.
              <span style={{ color: "#6b7280" }}> Example: pattern "\b(premium|luxury|high-end)\b" finds positioning language.</span></div>
          </div>

          <div style={{ fontWeight: 700, marginBottom: 6, color: "#111827" }}>How to Add a Metric</div>
          <ol style={{ margin: 0, paddingLeft: 20, marginBottom: 12 }}>
            <li>Click <strong>+ Add Metric</strong></li>
            <li>Choose a name (e.g., "Physical Store Recommendations")</li>
            <li>Select a type (Keyword Count, Co-Mention, or Regex Match)</li>
            <li>Fill in the parameters (comma-separated keywords, competitor names, or regex pattern)</li>
            <li>Choose scope: <strong>Global</strong> (all brands) or <strong>Brand</strong> (specific brand only)</li>
            <li>Click <strong>Create</strong></li>
          </ol>

          <div style={{ fontWeight: 700, marginBottom: 6, color: "#111827" }}>What Happens Next</div>
          <p style={{ margin: "0 0 10px" }}>
            When a user starts a new copilot session, all active metrics (built-in + custom) run automatically.
            Results are injected into the copilot's context as <strong>[EXACT - pre-computed]</strong> tables.
            The LLM is instructed to use these numbers directly - no manual recounting, no approximations.
          </p>

          <div style={{ fontWeight: 700, marginBottom: 6, color: "#111827" }}>Tips</div>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li>Use <strong>Keyword Count</strong> for simple term matching (case-insensitive by default)</li>
            <li>Use <strong>Co-Mention</strong> to track competitive overlap - which models mention your brand alongside competitors</li>
            <li>Use <strong>Regex Match</strong> for complex patterns (e.g., price ranges, specific phrases with variations)</li>
            <li>Disable a metric (instead of deleting) to keep it for later reuse</li>
            <li>Custom metrics don't affect existing sessions - only new ones</li>
          </ul>
        </div>
      )}
    </div>
  );
}

// All types - for display labels on cards
const METRIC_TYPE_LABELS = {
  keyword_count: "Keyword Count",
  brand_mention: "Brand Mention",
  url_domain_count: "URL/Domain Count",
  source_categories: "Source Categories",
  co_mention: "Co-Mention",
  regex_match: "Regex Match",
  summary: "Summary Stats",
  index: "Index",
  llm_extraction: "LLM Extraction",
};

// Only types users can create custom metrics for
const CREATABLE_METRIC_TYPES = {
  keyword_count: "Keyword Count",
  co_mention: "Co-Mention",
  regex_match: "Regex Match",
};

function MetricCard({ metric: m, onToggle, onDelete }) {
  const [confirmDel, setConfirmDel] = useState(false);
  const isLlmExtraction = m.metric_type === "llm_extraction";

  return (
    <div style={{
      background: m.is_builtin ? "#fafbff" : "#fff",
      border: m.is_builtin ? "1px solid #e0e7ff" : "1px solid var(--border)",
      borderRadius: 10,
      padding: "12px 16px", display: "flex", alignItems: "center", gap: 12,
      opacity: m.is_active ? 1 : 0.6,
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: "#111827" }}>
          {m.name}
          {m.is_builtin && (
            <span style={{ marginLeft: 8, fontSize: 10, background: isLlmExtraction ? "#fef3c7" : "#e0e7ff", color: isLlmExtraction ? "#92400e" : "#3730a3", padding: "2px 6px", borderRadius: 4, fontWeight: 700 }}>
              {isLlmExtraction ? "LLM EXTRACTION" : "PYTHON"}
            </span>
          )}
          {!m.is_builtin && (
            <span style={{ marginLeft: 8, fontSize: 10, background: "#f0fdf4", color: "#166534", padding: "2px 6px", borderRadius: 4, fontWeight: 700 }}>CUSTOM</span>
          )}
          <span style={{ marginLeft: 8, fontSize: 10, background: "#f3f4f6", color: "#6b7280", padding: "2px 6px", borderRadius: 4, fontWeight: 600 }}>
            {METRIC_TYPE_LABELS[m.metric_type] || m.metric_type}
          </span>
        </div>
        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4, fontFamily: "monospace" }}>
          {JSON.stringify(m.config_json)}
        </div>
        <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
          {m.is_builtin
            ? "Always active - runs automatically at session creation"
            : `Scope: ${m.scope}${m.scope_id ? ` (brand #${m.scope_id})` : ""} | Created: ${m.created_at ? new Date(m.created_at).toLocaleDateString() : "-"}`
          }
        </div>
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <button
          className="btn-secondary"
          style={{ padding: "4px 10px", fontSize: 12 }}
          onClick={onToggle}
        >
          {m.is_active ? "Disable" : "Enable"}
        </button>
        {!m.is_builtin && (
          confirmDel ? (
            <>
              <button className="btn-danger" style={{ padding: "4px 10px", fontSize: 12 }} onClick={onDelete}>Delete</button>
              <button className="btn-secondary" style={{ padding: "4px 8px", fontSize: 12 }} onClick={() => setConfirmDel(false)}>Cancel</button>
            </>
          ) : (
            <button
              className="btn-secondary"
              style={{ padding: "4px 10px", fontSize: 12, color: "#ef4444", borderColor: "#fca5a5" }}
              onClick={() => setConfirmDel(true)}
            >
              Delete
            </button>
          )
        )}
      </div>
    </div>
  );
}

function MetricCreateForm({ metricTypes, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", metric_type: "keyword_count", scope: "global",
  });
  const [configFields, setConfigFields] = useState({ keywords: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setConfig = (k, v) => setConfigFields((f) => ({ ...f, [k]: v }));

  const handleTypeChange = (type) => {
    set("metric_type", type);
    // Reset config fields for the new type
    if (type === "keyword_count") setConfigFields({ keywords: "", count_mode: "answers_containing" });
    else if (type === "co_mention") setConfigFields({ brand: "", competitors: "" });
    else if (type === "regex_match") setConfigFields({ pattern: "" });
    else setConfigFields({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSaving(true);

    // Build config_json from fields
    let config = {};
    if (form.metric_type === "keyword_count") {
      config.keywords = (configFields.keywords || "").split(",").map((s) => s.trim()).filter(Boolean);
      config.count_mode = configFields.count_mode || "answers_containing";
    } else if (form.metric_type === "co_mention") {
      config.brand = (configFields.brand || "").trim();
      config.competitors = (configFields.competitors || "").split(",").map((s) => s.trim()).filter(Boolean);
    } else if (form.metric_type === "regex_match") {
      config.pattern = (configFields.pattern || "").trim();
    }

    try {
      await onCreated({ ...form, config_json: config });
      setForm({ name: "", metric_type: "keyword_count", scope: "global" });
      setConfigFields({ keywords: "" });
      setOpen(false);
    } catch (err) {
      setError(err?.message || "Failed to create metric");
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    border: "1px solid #d1d5db", borderRadius: 6, padding: "6px 10px",
    fontSize: 13, width: "100%", boxSizing: "border-box",
  };

  if (!open) {
    return (
      <button className="btn-primary" style={{ padding: "6px 14px", fontSize: 13 }} onClick={() => setOpen(true)}>
        + Add Metric
      </button>
    );
  }

  return (
    <form onSubmit={handleSubmit} style={{
      background: "#f9fafb", border: "1px solid var(--border)", borderRadius: 10,
      padding: 16, display: "grid", gap: 10, marginBottom: 12,
    }}>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 200px" }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Name *</label>
          <input style={inputStyle} value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Physical Store Recommendations" required />
        </div>
        <div style={{ flex: "0 0 180px" }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Type *</label>
          <select style={inputStyle} value={form.metric_type} onChange={(e) => handleTypeChange(e.target.value)}>
            {Object.entries(CREATABLE_METRIC_TYPES).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
        <div style={{ flex: "0 0 120px" }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Scope</label>
          <select style={inputStyle} value={form.scope} onChange={(e) => set("scope", e.target.value)}>
            <option value="global">Global</option>
            <option value="brand">Brand</option>
          </select>
        </div>
      </div>

      {form.metric_type === "keyword_count" && (
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Keywords (comma-separated) *</label>
          <input style={inputStyle} value={configFields.keywords || ""} onChange={(e) => setConfig("keywords", e.target.value)} placeholder="visit store, physical location, in-person" required />
        </div>
      )}
      {form.metric_type === "co_mention" && (
        <>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Brand name *</label>
            <input style={inputStyle} value={configFields.brand || ""} onChange={(e) => setConfig("brand", e.target.value)} placeholder="Medikali" required />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Competitors (comma-separated) *</label>
            <input style={inputStyle} value={configFields.competitors || ""} onChange={(e) => setConfig("competitors", e.target.value)} placeholder="BioGaia, Linex, Enterogermina" required />
          </div>
        </>
      )}
      {form.metric_type === "regex_match" && (
        <div>
          <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Regex pattern *</label>
          <input style={inputStyle} value={configFields.pattern || ""} onChange={(e) => setConfig("pattern", e.target.value)} placeholder="\b(visit|store|location)\b" required />
        </div>
      )}

      <div style={{ display: "flex", gap: 8 }}>
        <button className="btn-primary" style={{ padding: "6px 14px", fontSize: 13 }} type="submit" disabled={saving}>
          {saving ? "Creating..." : "Create"}
        </button>
        <button className="btn-secondary" style={{ padding: "6px 12px", fontSize: 13 }} type="button" onClick={() => setOpen(false)}>Cancel</button>
      </div>
      {error && <div style={{ fontSize: 12, color: "#ef4444" }}>{error}</div>}
    </form>
  );
}

// ---------------------------------------------------------------------------
// Invitations section
// ---------------------------------------------------------------------------

function InviteForm({ onInvite }) {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!open) {
    return (
      <button
        className="btn-primary"
        style={{ padding: "6px 14px", fontSize: 13 }}
        onClick={() => setOpen(true)}
      >
        + Invite User
      </button>
    );
  }

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        if (!email.trim()) return;
        setBusy(true);
        setError("");
        try {
          await onInvite(email.trim(), isAdmin);
          setEmail("");
          setIsAdmin(false);
          setOpen(false);
        } catch (err) {
          setError(err?.message || "Failed");
        } finally {
          setBusy(false);
        }
      }}
      style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}
    >
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="email@example.com"
        required
        style={{ padding: "6px 10px", fontSize: 13, borderRadius: 6, border: "1px solid #d1d5db", width: 220 }}
      />
      <label style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
        <input type="checkbox" checked={isAdmin} onChange={(e) => setIsAdmin(e.target.checked)} />
        Admin
      </label>
      <button className="btn-primary" style={{ padding: "6px 14px", fontSize: 13 }} type="submit" disabled={busy}>
        {busy ? "Sending..." : "Send"}
      </button>
      <button className="btn-secondary" style={{ padding: "6px 12px", fontSize: 13 }} type="button" onClick={() => setOpen(false)}>
        Cancel
      </button>
      {error && <span style={{ fontSize: 12, color: "#ef4444" }}>{error}</span>}
    </form>
  );
}

function InvitationsSection({ invitations, loading, onInvite, onRevoke, onResend, thStyle }) {
  const statusBadge = (status) => {
    const map = {
      pending: { bg: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe", label: "Pending" },
      accepted: { bg: "#ecfdf5", color: "#065f46", border: "#a7f3d0", label: "Accepted" },
      expired: { bg: "#f9fafb", color: "#6b7280", border: "#e5e7eb", label: "Expired" },
    };
    const s = map[status] || map.expired;
    return (
      <span style={{
        display: "inline-block", fontSize: 11, fontWeight: 600,
        padding: "2px 8px", borderRadius: 6,
        background: s.bg, color: s.color, border: `1px solid ${s.border}`,
      }}>
        {s.label}
      </span>
    );
  };

  const tdStyle = { padding: "8px 12px", fontSize: 13, borderBottom: "1px solid var(--border)" };

  return (
    <section style={{ marginTop: 36 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>
          Invitations ({invitations.filter((i) => i.status === "pending").length} pending)
        </h2>
        <InviteForm onInvite={onInvite} />
      </div>

      {loading ? (
        <div style={{ color: "#9ca3af", fontSize: 13 }}>Loading...</div>
      ) : invitations.length === 0 ? (
        <div style={{ color: "#9ca3af", fontSize: 13, padding: "12px 0" }}>
          No invitations yet. Click "+ Invite User" to send one.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff", border: "1px solid var(--border)", borderRadius: 10 }}>
            <thead>
              <tr>
                <th style={thStyle}>Email</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Admin</th>
                <th style={{ ...thStyle, textAlign: "center" }}>Status</th>
                <th style={thStyle}>Invited By</th>
                <th style={thStyle}>Sent</th>
                <th style={thStyle}>Expires</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {invitations.map((inv) => (
                <tr key={inv.id}>
                  <td style={tdStyle}>{inv.email}</td>
                  <td style={{ ...tdStyle, textAlign: "center" }}>{inv.is_admin ? "Yes" : "No"}</td>
                  <td style={{ ...tdStyle, textAlign: "center" }}>{statusBadge(inv.status)}</td>
                  <td style={tdStyle}>{inv.invited_by}</td>
                  <td style={tdStyle}>{inv.created_at ? new Date(inv.created_at).toLocaleDateString() : "-"}</td>
                  <td style={tdStyle}>{inv.expires_at ? new Date(inv.expires_at).toLocaleDateString() : "-"}</td>
                  <td style={tdStyle}>
                    {inv.status === "pending" && (
                      <div style={{ display: "flex", gap: 6 }}>
                        <button
                          className="btn-secondary"
                          style={{ padding: "4px 10px", fontSize: 12 }}
                          onClick={() => onResend(inv.id)}
                        >
                          Resend
                        </button>
                        <button
                          className="btn-secondary"
                          style={{ padding: "4px 10px", fontSize: 12, color: "#dc2626", borderColor: "#fecaca" }}
                          onClick={() => { if (window.confirm(`Revoke invitation for ${inv.email}?`)) onRevoke(inv.id); }}
                        >
                          Revoke
                        </button>
                      </div>
                    )}
                    {inv.status === "accepted" && (
                      <span style={{ fontSize: 12, color: "#059669" }}>Joined {inv.accepted_at ? new Date(inv.accepted_at).toLocaleDateString() : ""}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Documentation Tab
// ---------------------------------------------------------------------------

const DOC_CATEGORIES = [
  {
    id: "platform",
    title: "Platform Reference",
    icon: "\u2699\uFE0F",
    description: "User roles, permissions, and AI prompt configuration",
    items: [
      { id: "roles", label: "User Roles & Permissions", type: "inline" },
      { id: "prompts", label: "AI Prompts Reference", type: "inline" },
    ],
  },
  {
    id: "architecture",
    title: "Architecture",
    icon: "\uD83C\uDFD7\uFE0F",
    description: "System design, data flow pipelines, and module diagrams",
    items: [
      { label: "Data Model", file: "data-model.html", type: "doc" },
      { label: "3D Data Flow", file: "architecture-3d.html", type: "doc" },
      { label: "LLM Pipeline Diagrams", file: "architecture-diagrams.html", type: "doc" },
      { label: "ADS Pipeline Diagrams", file: "ads-diagrams.html", type: "doc" },
      { label: "Headline KPI Formulas", file: "kpi-formulas.html", type: "doc" },
    ],
  },
  {
    id: "operations",
    title: "Operations",
    icon: "\uD83D\uDE80",
    description: "Deployment, environments, backup strategy, and release management",
    items: [
      { label: "Deployment Guide", file: "deployment-guide.html", type: "doc" },
    ],
  },
  {
    id: "quality",
    title: "Quality Assurance",
    icon: "\u2705",
    description: "Testing strategy, automated test coverage, and QA checklists",
    items: [
      { label: "Testing Strategy", file: "testing-strategy.html", type: "doc" },
      { label: "Test Coverage Report", file: "test-coverage.html", type: "doc" },
    ],
  },
];

// Keep for backwards compat with the inline section renderer
const DOC_SECTIONS = [
  { id: "roles", label: "User Roles & Permissions" },
  { id: "prompts", label: "AI Prompts Reference" },
  { id: "architecture", label: "Architecture Overview" },
];

const ROLE_MATRIX = {
  columns: ["Permission", "Admin", "Analyst", "Viewer"],
  rows: [
    { category: "Authentication & Account" },
    ["Login / Logout", "Yes", "Yes", "Yes"],
    ["Change own password", "Yes", "Yes", "Yes"],
    ["Setup 2FA", "Yes", "Yes", "Yes"],
    ["Reset other users' 2FA", "Yes", "No", "No"],

    { category: "User Management" },
    ["Invite users", "Yes", "No", "No"],
    ["Create / edit / deactivate users", "Yes", "No", "No"],
    ["Assign roles", "Yes", "No", "No"],
    ["View user list", "Yes", "No", "No"],

    { category: "Brand Management" },
    ["Create brands", "Yes", "Yes", "No"],
    ["Delete brands", "Yes", "No", "No"],
    ["Deactivate brands", "Yes", "Yes", "No"],
    ["Edit brand name / settings", "Yes", "Yes", "No"],
    ["Toggle module access (LLM/ADS)", "Yes", "No", "No"],
    ["View brands (assigned only)", "Yes", "Yes", "Yes"],

    { category: "LLM Discovery" },
    ["Create / configure audits", "Yes", "Yes", "No"],
    ["Run audits", "Yes", "Yes", "No"],
    ["Cancel running audits", "Yes", "Yes", "No"],
    ["View audit matrix", "Yes", "Yes", "Yes"],
    ["Edit questions (CSV upload)", "Yes", "Yes", "No"],
    ["Start MAD Pilot sessions", "Yes", "Yes", "No"],
    ["View MAD Pilot sessions", "Yes", "Yes", "Yes"],
    ["Export PPTX", "Yes", "Yes", "Yes"],

    { category: "ADS Discovery" },
    ["Run ad ingestion", "Yes", "Yes", "No"],
    ["View ads matrix", "Yes", "Yes", "Yes"],
    ["Use MAD Pilot (ADS chat)", "Yes", "Yes", "No"],
    ["Manage competitors", "Yes", "Yes", "No"],
    ["Capture screenshots", "Yes", "Yes", "No"],

    { category: "Settings & Configuration" },
    ["Edit global settings (prompts, models)", "Yes", "No", "No"],
    ["Edit brand-level settings", "Yes", "Yes", "No"],
    ["View settings", "Yes", "Yes", "Yes"],

    { category: "Cost & Billing" },
    ["View Cost Control page", "Yes", "No", "No"],
    ["Edit model prices", "Yes", "No", "No"],
    ["View own session costs", "Yes", "Yes", "Yes"],

    { category: "Citations Module" },
    ["View citations (all brands)", "Yes", "Yes", "Yes"],
    ["Filter by brand / audit / category", "Yes", "Yes", "Yes"],
    ["View domain detail / URL drilldown", "Yes", "Yes", "Yes"],
    ["Edit Mode (change categories)", "Yes", "No", "No"],
    ["Reclassify domains (LLM pipeline)", "Yes", "No", "No"],
    ["Toggle owned domain status", "Yes", "No", "No"],
    ["Trigger citation backfill", "Yes", "No", "No"],

    { category: "Analysis Hub" },
    ["View analysis history", "Yes", "Yes", "Yes"],
    ["View analysis report", "Yes", "Yes", "Yes"],
    ["View questions / context blob", "Yes", "Yes", "Yes"],
    ["Export PPTX / Markdown", "Yes", "Yes", "Yes"],

    { category: "Admin & System" },
    ["Access Admin Management", "Yes", "No", "No"],
    ["View system info", "Yes", "No", "No"],
    ["Configure copilot metrics", "Yes", "No", "No"],
    ["Manage release calendar", "Yes", "No", "No"],
    ["View Documentation Hub", "Yes", "Yes", "No"],
  ],
};

// ---------------------------------------------------------------------------
// Architecture interactive documentation
// ---------------------------------------------------------------------------

const ARCH_PHASES = [
  {
    id: "phase1",
    title: "Phase 1 - Audit Execution",
    color: "#2563eb",
    summary: "User creates an audit: selects questions (up to 30) and LLM models. Each question is sent to each model in parallel, producing an answer matrix.",
    diagram: [
      { label: "Brand + Questions + Models", type: "input" },
      { label: "Parallel LLM Execution\n(asyncio.Semaphore(5))", type: "process" },
      { label: "Answer Matrix\n(25Q x ~4M = ~100 answers)", type: "output" },
    ],
    details: [
      { label: "Parallel execution", value: "asyncio.Semaphore(5) - 5 concurrent LLM calls per run" },
      { label: "Location awareness", value: "Questions localized (e.g., 'Answer for users in Cluj-Napoca, Romania')" },
      { label: "Language detection", value: "langdetect identifies question and answer language" },
      { label: "Cancellation", value: "Checked every 5 completions - user can stop mid-run" },
      { label: "Cost tracking", value: "Per-attempt via estimate_cost(model, prompt_tokens, completion_tokens)" },
      { label: "Progress", value: "SSE streaming events: start, progress, done, cancelled" },
    ],
    models: [
      { provider: "OpenAI", models: "gpt-4o, gpt-4.1, gpt-4.1-mini, gpt-5, gpt-5-mini", search: "Responses API" },
      { provider: "Google", models: "gemini-2.5-pro, gemini-2.5-flash", search: "Google Search" },
      { provider: "Perplexity", models: "sonar, sonar-pro, sonar-reasoning", search: "Built-in" },
      { provider: "SearchAPI", models: "searchapi-ai-overview", search: "Google AI Overview" },
      { provider: "Anthropic", models: "claude-sonnet-4-6, claude-opus-4-6, claude-haiku-4-5", search: "None" },
    ],
    files: ["src/app/services/sra_service.py", "src/app/services/llm_client.py", "src/app/routes/audits.py"],
  },
  {
    id: "phase2",
    title: "Phase 2 - Context Engineering",
    color: "#7c3aed",
    summary: "When MAD Pilot starts, the system builds a three-layer context blob (~800K chars) from the answer matrix. This is the intelligence foundation for AI analysis.",
    diagram: [
      { label: "Answer Matrix", type: "input" },
      { label: "Layer 1a: Python Metrics\n[EXACT, free, instant]", type: "layer", color: "#16a34a" },
      { label: "Layer 1b: LLM MapReduce\n[AI, entity-resolved, ~$0.02]", type: "layer", color: "#2563eb" },
      { label: "Cross-Validation\n(Python vs LLM counts)", type: "check" },
      { label: "Layer 2: Structured Extractions\n[AI, per-question]", type: "layer", color: "#7c3aed" },
      { label: "Layer 3: Raw Answers\n[UNMODIFIED, for quotes only]", type: "layer", color: "#dc2626" },
      { label: "Context Blob\n(~800K chars)", type: "output" },
    ],
    layers: [
      {
        name: "Layer 1a - Python Metrics [EXACT]",
        color: "#16a34a",
        desc: "Computed instantly, zero cost, deterministic. Pre-computed numbers are sacred - LLM cannot override these.",
        items: ["Audit overview (questions, models, answers, errors)", "Question index (Q1...QN mapping)", "Brand Mentions per Model (string matching + aliases)", "URL/Domain Inventory (per model, normalized, filtered)", "Source Categories (self-growing: heuristic + LLM + admin overrides)", "Co-Mention Matrix (brand pairs per model)", "Custom metrics (admin-configured)"],
      },
      {
        name: "Layer 1b - LLM MapReduce [AI]",
        color: "#2563eb",
        desc: "Map: gpt-4.1-mini per question (5 concurrent). Reduce: Python aggregation across all questions.",
        items: ["All Brand Mentions per Model (entity-resolved, with roles)", "Product Brand Mentions (parent linkage)", "Brand x Brand Co-occurrence", "Domain x Brand Cross-Reference", "Brand-Owned Domain Citations", "Sentiment Toward Brand (per model)", "Consensus Distribution (HIGH/MEDIUM/LOW)"],
      },
      {
        name: "Layer 2 - Structured Extractions [AI]",
        color: "#7c3aed",
        desc: "Per question, per model: brands (name, role, type, occurrence_count), sentiment, key claims, URLs, consensus.",
        items: ["Brands mentioned (entity-resolved, with brand_role)", "Sentiment toward audited brand", "Key claims extracted", "URLs found (url, domain, category, refers_to_brand)", "Consensus across models (HIGH/MEDIUM/LOW)"],
      },
      {
        name: "Layer 3 - Raw Answers [UNMODIFIED]",
        color: "#dc2626",
        desc: "Complete model responses in original form. Used for direct quotes ONLY - never count from here.",
        items: ["Full answer text per model per question", "XML boundary tags for parsing", "Ground truth for quote verification"],
      },
    ],
    crossValidation: {
      title: "Cross-Validation Gate",
      desc: "After both Layer 1a (Python) and Layer 1b (LLM MapReduce) produce brand mention counts, the system compares them. This catches entity resolution errors before they reach the analyst.",
      steps: [
        { label: "Python count", desc: "Simple string matching + aliases. Fast, deterministic, but misses entity variants (e.g., 'BT' = 'Banca Transilvania').", color: "#16a34a" },
        { label: "LLM count", desc: "Entity-resolved via gpt-4.1-mini extraction. Understands that 'BT', 'Banca Transilvania', 'BT Express' are related. Smarter but non-deterministic.", color: "#2563eb" },
        { label: "Compare", desc: "For the audited brand, compare Python count vs LLM count. If difference >30%, a 'Data Quality Notes' warning is injected into the context blob.", color: "#d97706" },
      ],
      example: "Python finds 'Expresso' 127 times (exact string match). LLM finds 143 mentions (includes 'BRD Expresso', 'Creditul Expresso' as the same entity). Difference: 12.6% - within tolerance, no warning.",
      whyBoth: "Python counts are used as ground truth in tables (exact, reproducible). LLM counts add entity-resolved intelligence. The cross-validation ensures they don't diverge wildly - if they do, something is wrong with the extraction prompt or the brand aliases.",
    },
    domainClassification: {
      title: "Domain Classification Engine (Self-Growing)",
      desc: "Every URL domain encountered in audit answers is classified into a source category. The system uses a three-tier approach that learns over time - after a few audits, most domains are already cached.",
      tiers: [
        { label: "1. DB Cache", desc: "Check domain_categories table. Admin overrides are never changed. Previously classified domains return instantly.", color: "#16a34a", icon: "\u26A1" },
        { label: "2. Heuristic Patterns", desc: "Hardcoded pattern matching: social platforms, forums, news sites, e-commerce keywords. Results saved to DB for future reuse.", color: "#2563eb", icon: "\u2699" },
        { label: "3. LLM Batch Call", desc: "Unknown domains sent to gpt-4.1-mini in one batch call (~$0.001). Results saved to DB permanently. Same domain never classified twice.", color: "#7c3aed", icon: "\uD83E\uDD16" },
      ],
      categories: ["brand_site", "comparison_aggregator", "news_editorial", "social_video", "forum", "e_commerce", "authority", "other"],
      keyPoints: [
        "The cache is permanent - it only grows, never deletes",
        "Admin overrides (source='admin') are never overwritten by auto or LLM",
        "After a few audits in any vertical (banking, pharma, auto), most domains are cached",
        "New verticals trigger LLM classification only for their unique domains",
        "Typical cost: $0.001 per audit for unknown domains, trending to $0 over time",
      ],
    },
    files: ["src/app/services/copilot_service.py", "src/app/services/copilot_extraction.py", "src/app/services/copilot_metrics.py"],
  },
  {
    id: "phase3",
    title: "Phase 3 - AI Analysis + Quality Gates",
    color: "#dc2626",
    summary: "MAD Pilot generates a 10-section analysis. Every response passes through 4 automated quality checks with auto-correction and two-pass error fixing.",
    diagram: [
      { label: "Enforced Rules + Analyst Prompt\n+ Session Summary + Context Blob", type: "input" },
      { label: "LLM Analysis\n(streaming SSE)", type: "process" },
      { label: "Validator (4 checks)", type: "check" },
      { label: "Auto-correct / Two-pass fix", type: "process" },
      { label: "Quality Badge + Report", type: "output" },
      { label: "Session Summary Update\n(gpt-4.1-mini, async)", type: "process" },
    ],
    sections: [
      { num: 1, title: "Brand & Competitor Mentions", tables: "1.0 Python vs LLM Comparison, 1.1 Count, 1.1b Presence, 1.2 Share %, 1.3 LLM Share, 1.4 Products, 1.5 Co-occurrence" },
      { num: 2, title: "Co-Mentions & Mental Competition", tables: "2.1 Count, 2.2 %, Qualitative top 5" },
      { num: 3, title: "Total Cited Sources", tables: "3.1-3.3 Per LLM, 3.4 Blended" },
      { num: 4, title: "Source Categories & Clusters", tables: "4.1-4.3 Per LLM, 4.4 Blended" },
      { num: 5, title: "Domains per Industry", tables: "5.1-5.3 Per LLM, 5.4 Blended" },
      { num: 6, title: "Domains per Brand per LLM", tables: "6.1-6.4 Per LLM, 6.5 Blended" },
      { num: 7, title: "Qualitative Clusters", tables: "Trends, correlations, domain groupings" },
      { num: 8, title: "Brand Pillars vs LLM Output", tables: "8.1 Brand-owned citations, gap analysis" },
      { num: 9, title: "Sentiment & Tone", tables: "9.1 Positive/neutral/negative buckets" },
      { num: 10, title: "Final Interpretation", tables: "Mental space, competitor positioning, GEO levers" },
    ],
    checks: [
      { name: "Section Completeness", desc: "All 10 sections present? Missing <=2: WARNING, Missing >2: ERROR" },
      { name: "Table Number Accuracy", desc: "Compare brand totals vs pre-computed data. Diff <=2: AUTO-CORRECT, >10%: ERROR" },
      { name: "Percentage Validation", desc: "Column sums ~100%? Skip per-row/co-mention tables. Tolerance: 5% (10% with Others)" },
      { name: "Quote Verification", desc: "Fuzzy-match against Layer 3 raw answers. Diacritic-normalized. >40% unverified: ERROR" },
    ],
    severity: [
      { level: "auto_correct", badge: "None (silent)", behavior: "Fix applied automatically", example: "Brand total off by 1" },
      { level: "warning", badge: "Yellow dot", behavior: "Shown in report, export allowed", example: "% column sums to 87%" },
      { level: "error", badge: "Red dot", behavior: "Triggers two-pass LLM correction", example: "3+ sections missing" },
    ],
    files: ["src/app/services/copilot_service.py", "src/app/services/copilot_validator.py"],
  },
];

const COST_TABLE = [
  { component: "Audit execution", model: "gpt-4o, gemini, sonar, etc.", when: "Per question per model", cost: "$0.001-0.05/answer" },
  { component: "AI Overview", model: "searchapi-ai-overview", when: "Per question", cost: "$0.02/request" },
  { component: "Extraction (map)", model: "gpt-4.1-mini", when: "Per question (25x)", cost: "~$0.01-0.03 total" },
  { component: "Analysis (main)", model: "gpt-4.1 (default)", when: "Per session start", cost: "~$0.05-0.15" },
  { component: "Two-pass correction", model: "Same as analysis", when: "Only if errors found", cost: "~$0.03-0.08" },
  { component: "Session summary", model: "gpt-4.1-mini", when: "Per response", cost: "~$0.001" },
  { component: "Follow-up responses", model: "Analysis model", when: "Per user message", cost: "~$0.02-0.08" },
];

const DESIGN_DECISIONS = [
  { title: "Three-layer separation", desc: "Python handles counting (deterministic), AI handles entity resolution (semantic), raw answers provide ground truth for quotes." },
  { title: "Pre-computed numbers are sacred", desc: "The enforced prompt tells the model: 'If your analysis contradicts a pre-computed number, the pre-computed number is correct.'" },
  { title: "MapReduce for extraction", desc: "Map phase (per question, parallel, cheap model) + Reduce phase (Python aggregation, instant) gives granularity and accuracy at low cost." },
  { title: "Validation on every response", desc: "Not just the initial analysis. Follow-up questions are also validated, maintaining quality throughout the conversation." },
  { title: "Addendum-based correction", desc: "Corrections are appended, not replaced. Preserves the original analysis while adding fixes for specific errors." },
  { title: "Session summary prevents drift", desc: "Long conversations don't lose track. Summary is updated after every response and injected into the next prompt." },
  { title: "Brand confirmation before analysis", desc: "User validates brand name + aliases before context is built, preventing entity resolution errors from propagating." },
  { title: "Google internal URLs filtered", desc: "vertexaisearch.cloud.google.com grounding redirects stripped from all domain counts and tables." },
];

function ArchitectureDoc() {
  const [expandedPhase, setExpandedPhase] = useState("phase2");

  const cardBorder = (color) => ({ borderLeft: `4px solid ${color}`, background: "#fff", border: `1px solid #e5e7eb`, borderLeftColor: color, borderRadius: 10, padding: 18, marginBottom: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.04)" });
  const tagStyle = (bg, fg) => ({ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: bg, color: fg, textTransform: "uppercase", letterSpacing: "0.05em" });

  const FlowDiagram = ({ steps }) => (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, margin: "16px 0" }}>
      {steps.map((s, i) => {
        const colors = { input: { bg: "#eff6ff", fg: "#1d4ed8", border: "#bfdbfe" }, process: { bg: "#f0fdf4", fg: "#166534", border: "#bbf7d0" }, output: { bg: "#fefce8", fg: "#854d0e", border: "#fde68a" }, layer: { bg: "#faf5ff", fg: "#7c3aed", border: "#e9d5ff" }, check: { bg: "#fff7ed", fg: "#c2410c", border: "#fed7aa" } };
        const c = colors[s.type] || colors.process;
        return (
          <React.Fragment key={i}>
            {i > 0 && <span style={{ fontSize: 18, color: "#9ca3af" }}>{"\u2192"}</span>}
            <div style={{ padding: "8px 14px", borderRadius: 8, background: s.color ? `${s.color}10` : c.bg, border: `1px solid ${s.color || c.border}`, color: s.color || c.fg, fontSize: 12, fontWeight: 600, textAlign: "center", whiteSpace: "pre-line", minWidth: 100 }}>
              {s.label}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );

  return (
    <section>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: "#1a1f36", marginBottom: 4 }}>
        LLM Discovery - Data & Information Journey
      </h2>
      <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 12, lineHeight: 1.5 }}>
        Three-phase pipeline: <strong>Audit Execution</strong> {"\u2192"} <strong>Context Engineering</strong> {"\u2192"} <strong>AI Analysis + Quality Gates</strong>. Click each phase to explore.
      </p>

      {/* Phase cards */}
      {ARCH_PHASES.map((phase) => {
        const isOpen = expandedPhase === phase.id;
        return (
          <div key={phase.id} style={{ ...cardBorder(phase.color), borderLeftWidth: 4, cursor: "pointer", marginBottom: 14 }}>
            <div onClick={() => setExpandedPhase(isOpen ? null : phase.id)} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: phase.color }}>{phase.title}</h3>
                <p style={{ margin: "4px 0 0", fontSize: 13, color: "#6b7280", lineHeight: 1.5 }}>{phase.summary}</p>
              </div>
              <span style={{ fontSize: 20, color: "#9ca3af", transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>{"\u25BC"}</span>
            </div>

            {isOpen && (
              <div style={{ marginTop: 16 }} onClick={(e) => e.stopPropagation()}>
                {/* Flow diagram */}
                <FlowDiagram steps={phase.diagram} />

                {/* Phase 1 specifics */}
                {phase.models && (
                  <>
                    <h4 style={{ fontSize: 13, fontWeight: 700, color: "#1a1f36", marginTop: 16, marginBottom: 8 }}>Supported Models</h4>
                    <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                        <thead><tr style={{ background: "#f8fafc" }}>
                          {["Provider", "Models", "Web Search"].map((h) => <th key={h} style={{ padding: "6px 12px", textAlign: "left", fontWeight: 700, color: "#374151", borderBottom: "1px solid #e5e7eb" }}>{h}</th>)}
                        </tr></thead>
                        <tbody>{phase.models.map((m, i) => (
                          <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                            <td style={{ padding: "6px 12px", fontWeight: 600 }}>{m.provider}</td>
                            <td style={{ padding: "6px 12px", fontFamily: "monospace", fontSize: 11 }}>{m.models}</td>
                            <td style={{ padding: "6px 12px" }}>{m.search}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </div>
                  </>
                )}

                {/* Phase 1 details */}
                {phase.details && (
                  <>
                    <h4 style={{ fontSize: 13, fontWeight: 700, color: "#1a1f36", marginTop: 16, marginBottom: 8 }}>Key Details</h4>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 8 }}>
                      {phase.details.map((d, i) => (
                        <div key={i} style={{ padding: "8px 12px", background: "#f9fafb", borderRadius: 6, border: "1px solid #f1f5f9" }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: "#1a1f36" }}>{d.label}: </span>
                          <span style={{ fontSize: 12, color: "#374151" }}>{d.value}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Phase 2 layers */}
                {phase.layers && (
                  <>
                    <h4 style={{ fontSize: 13, fontWeight: 700, color: "#1a1f36", marginTop: 16, marginBottom: 8 }}>Context Layers (~800K chars total)</h4>
                    {phase.layers.map((layer, i) => (
                      <div key={i} style={{ borderLeft: `3px solid ${layer.color}`, paddingLeft: 14, marginBottom: 14 }}>
                        <h5 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: layer.color }}>{layer.name}</h5>
                        <p style={{ margin: "2px 0 6px", fontSize: 12, color: "#6b7280" }}>{layer.desc}</p>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {layer.items.map((item, j) => (
                            <span key={j} style={{ fontSize: 11, padding: "3px 8px", background: `${layer.color}10`, color: layer.color, borderRadius: 4, border: `1px solid ${layer.color}30` }}>{item}</span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {/* Cross-validation */}
                {phase.crossValidation && (
                  <>
                    <h4 style={{ fontSize: 13, fontWeight: 700, color: "#d97706", marginTop: 20, marginBottom: 8 }}>
                      {"\u26A0"} {phase.crossValidation.title}
                    </h4>
                    <p style={{ fontSize: 12, color: "#374151", lineHeight: 1.6, marginBottom: 12 }}>{phase.crossValidation.desc}</p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                      {phase.crossValidation.steps.map((step, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <span style={{ alignSelf: "center", fontSize: 18, color: "#9ca3af" }}>{i === 2 ? "\u2192" : "vs"}</span>}
                          <div style={{ flex: "1 1 180px", padding: "10px 14px", borderRadius: 8, borderLeft: `3px solid ${step.color}`, background: `${step.color}08`, border: `1px solid ${step.color}30`, borderLeftWidth: 3, borderLeftColor: step.color }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: step.color, marginBottom: 4 }}>{step.label}</div>
                            <div style={{ fontSize: 11, color: "#374151", lineHeight: 1.5 }}>{step.desc}</div>
                          </div>
                        </React.Fragment>
                      ))}
                    </div>
                    <div style={{ padding: 12, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, marginBottom: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#92400e", marginBottom: 4 }}>Example</div>
                      <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.5 }}>{phase.crossValidation.example}</div>
                    </div>
                    <div style={{ padding: 12, background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#0369a1", marginBottom: 4 }}>Why both methods?</div>
                      <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.5 }}>{phase.crossValidation.whyBoth}</div>
                    </div>
                  </>
                )}

                {/* Domain classification engine */}
                {phase.domainClassification && (
                  <>
                    <h4 style={{ fontSize: 13, fontWeight: 700, color: "#7c3aed", marginTop: 20, marginBottom: 8 }}>
                      {phase.domainClassification.title}
                    </h4>
                    <p style={{ fontSize: 12, color: "#374151", lineHeight: 1.6, marginBottom: 12 }}>{phase.domainClassification.desc}</p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 12 }}>
                      {phase.domainClassification.tiers.map((tier, i) => (
                        <div key={i} style={{ flex: "1 1 200px", padding: "10px 14px", borderRadius: 8, borderLeft: `3px solid ${tier.color}`, background: `${tier.color}08`, border: `1px solid ${tier.color}30`, borderLeftWidth: 3, borderLeftColor: tier.color }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: tier.color, marginBottom: 4 }}>{tier.icon} {tier.label}</div>
                          <div style={{ fontSize: 11, color: "#374151", lineHeight: 1.5 }}>{tier.desc}</div>
                        </div>
                      ))}
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 10 }}>
                      {phase.domainClassification.categories.map((cat) => (
                        <span key={cat} style={{ fontSize: 10, padding: "2px 8px", background: "#f3f4f6", border: "1px solid #e5e7eb", borderRadius: 4, color: "#374151", fontFamily: "monospace" }}>{cat}</span>
                      ))}
                    </div>
                    <div style={{ padding: 12, background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 8 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#0369a1", marginBottom: 6 }}>Key Points</div>
                      <ul style={{ margin: 0, paddingLeft: 16, fontSize: 12, color: "#374151", lineHeight: 1.7 }}>
                        {phase.domainClassification.keyPoints.map((p, i) => <li key={i}>{p}</li>)}
                      </ul>
                    </div>
                  </>
                )}

                {/* Phase 3 sections */}
                {phase.sections && (
                  <>
                    <h4 style={{ fontSize: 13, fontWeight: 700, color: "#1a1f36", marginTop: 16, marginBottom: 8 }}>10-Section Analysis Framework</h4>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: 6 }}>
                      {phase.sections.map((s) => (
                        <div key={s.num} style={{ padding: "6px 10px", background: "#f9fafb", borderRadius: 6, border: "1px solid #f1f5f9", display: "flex", gap: 8, alignItems: "baseline" }}>
                          <span style={{ ...tagStyle("#1a1f36", "#fff"), minWidth: 20, textAlign: "center" }}>S{s.num}</span>
                          <div>
                            <span style={{ fontSize: 12, fontWeight: 600, color: "#1a1f36" }}>{s.title}</span>
                            <span style={{ fontSize: 11, color: "#9ca3af", marginLeft: 6 }}>{s.tables}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Phase 3 quality checks */}
                {phase.checks && (
                  <>
                    <h4 style={{ fontSize: 13, fontWeight: 700, color: "#1a1f36", marginTop: 16, marginBottom: 8 }}>Quality Gate Pipeline</h4>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                      {phase.checks.map((c, i) => (
                        <div key={i} style={{ flex: "1 1 200px", padding: "10px 14px", background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 8 }}>
                          <div style={{ fontSize: 12, fontWeight: 700, color: "#c2410c", marginBottom: 4 }}>Check {i + 1}: {c.name}</div>
                          <div style={{ fontSize: 11, color: "#374151" }}>{c.desc}</div>
                        </div>
                      ))}
                    </div>
                    <h4 style={{ fontSize: 13, fontWeight: 700, color: "#1a1f36", marginTop: 12, marginBottom: 6 }}>Severity Model</h4>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {phase.severity.map((s) => (
                        <div key={s.level} style={{ padding: "6px 12px", borderRadius: 6, background: s.level === "error" ? "#fef2f2" : s.level === "warning" ? "#fffbeb" : "#f0fdf4", border: `1px solid ${s.level === "error" ? "#fecaca" : s.level === "warning" ? "#fde68a" : "#bbf7d0"}`, fontSize: 12 }}>
                          <strong style={{ color: s.level === "error" ? "#dc2626" : s.level === "warning" ? "#d97706" : "#16a34a" }}>{s.level}</strong>: {s.behavior} <span style={{ color: "#9ca3af" }}>({s.example})</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {/* Source files */}
                {phase.files && (
                  <div style={{ marginTop: 12, fontSize: 11, color: "#9ca3af" }}>
                    <strong>Source files:</strong> {phase.files.join(" · ")}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Cost structure */}
      <div style={{ ...cardBorder("#059669"), borderLeftWidth: 4, marginTop: 20 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#059669" }}>Cost Structure</h3>
        <p style={{ margin: "4px 0 12px", fontSize: 12, color: "#6b7280" }}>Typical full session cost: <strong>$0.10 - $0.40</strong> (audit execution separate)</p>
        <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead><tr style={{ background: "#f0fdf4" }}>
              {["Component", "Model", "When", "Typical Cost"].map((h) => <th key={h} style={{ padding: "6px 12px", textAlign: "left", fontWeight: 700, color: "#166534", borderBottom: "1px solid #bbf7d0" }}>{h}</th>)}
            </tr></thead>
            <tbody>{COST_TABLE.map((r, i) => (
              <tr key={i} style={{ borderBottom: "1px solid #f1f5f9" }}>
                <td style={{ padding: "6px 12px", fontWeight: 600 }}>{r.component}</td>
                <td style={{ padding: "6px 12px", fontFamily: "monospace", fontSize: 11 }}>{r.model}</td>
                <td style={{ padding: "6px 12px" }}>{r.when}</td>
                <td style={{ padding: "6px 12px", fontWeight: 600, color: "#059669" }}>{r.cost}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </div>

      {/* Design decisions */}
      <div style={{ marginTop: 20 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: "#1a1f36", marginBottom: 10 }}>Key Design Decisions</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 10 }}>
          {DESIGN_DECISIONS.map((d, i) => (
            <div key={i} style={{ padding: "12px 14px", background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#1a1f36", marginBottom: 4 }}>{i + 1}. {d.title}</div>
              <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.5 }}>{d.desc}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DocumentationTab() {
  const [docSection, setDocSection] = useState(null); // null = hub view

  const cellStyle = (isHeader, isYes, isNo) => ({
    padding: "8px 14px",
    fontSize: 13,
    fontWeight: isHeader ? 600 : 400,
    color: isHeader ? "#1a1f36" : isYes ? "#16a34a" : isNo ? "#dc2626" : "#6b7280",
    background: isHeader ? "#f8fafc" : "transparent",
    borderBottom: "1px solid #f1f5f9",
    textAlign: isHeader ? "left" : "center",
    whiteSpace: "nowrap",
  });

  // Hub view - documentation categories with cards
  if (!docSection) {
    return (
      <div style={{ marginTop: 10 }}>
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", marginBottom: 4 }}>Documentation Hub</h2>
          <p style={{ fontSize: 13, color: "#6b7280" }}>Technical documentation, architecture references, and operational guides for the MAD Discovery platform.</p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          {DOC_CATEGORIES.map((cat) => (
            <div
              key={cat.id}
              style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 12,
                padding: 20,
                transition: "box-shadow 0.2s",
              }}
              onMouseEnter={(e) => e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.06)"}
              onMouseLeave={(e) => e.currentTarget.style.boxShadow = "none"}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <span style={{ fontSize: 20 }}>{cat.icon}</span>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>{cat.title}</h3>
              </div>
              <p style={{ fontSize: 12, color: "#6b7280", marginBottom: 14, lineHeight: 1.5 }}>{cat.description}</p>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {cat.items.map((item, i) => {
                  if (item.type === "doc") {
                    return (
                      <a
                        key={i}
                        href={`/view-doc/${item.file}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "flex", alignItems: "center", gap: 8,
                          padding: "8px 12px", background: "#f8fafc", border: "1px solid #e5e7eb",
                          borderRadius: 8, textDecoration: "none", color: "#1e293b",
                          fontSize: 13, fontWeight: 500, transition: "all 0.15s",
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "#f1f5f9"; e.currentTarget.style.borderColor = "#cbd5e1"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "#f8fafc"; e.currentTarget.style.borderColor = "#e5e7eb"; }}
                      >
                        <span style={{ fontSize: 14, color: "#6b7280" }}>{"\u2197"}</span>
                        {item.label}
                        <span style={{ marginLeft: "auto", fontSize: 10, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px" }}>Opens in new tab</span>
                      </a>
                    );
                  }
                  return (
                    <button
                      key={i}
                      onClick={() => setDocSection(item.id)}
                      style={{
                        display: "flex", alignItems: "center", gap: 8,
                        padding: "8px 12px", background: "#f8fafc", border: "1px solid #e5e7eb",
                        borderRadius: 8, textDecoration: "none", color: "#1e293b",
                        fontSize: 13, fontWeight: 500, cursor: "pointer", textAlign: "left",
                        transition: "all 0.15s",
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "#f1f5f9"; e.currentTarget.style.borderColor = "#cbd5e1"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "#f8fafc"; e.currentTarget.style.borderColor = "#e5e7eb"; }}
                    >
                      <span style={{ fontSize: 14, color: "#6b7280" }}>{"\u2192"}</span>
                      {item.label}
                      <span style={{ marginLeft: "auto", fontSize: 10, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.5px" }}>View inline</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 10 }}>
      {/* Back to hub + section title */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <button
          onClick={() => setDocSection(null)}
          style={{
            padding: "6px 14px", fontSize: 13, fontWeight: 600,
            color: "#374151", background: "#f3f4f6", border: "1px solid #e5e7eb",
            borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 4,
          }}
        >
          {"\u2190"} Back to Documentation Hub
        </button>
      </div>

      {/* Roles matrix */}
      {docSection === "roles" && (
        <section>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: "#1a1f36", marginBottom: 6 }}>
            User Roles & Permissions Matrix
          </h2>
          <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 16, lineHeight: 1.5 }}>
            This matrix defines what each user role can do across the platform.
            Three roles are available: <strong>Admin</strong>, <strong>Analyst</strong>, and <strong>Viewer</strong>.
            Brand access is controlled per user - analysts and viewers only see their assigned brands.
          </p>

          <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 10 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 700 }}>
              <thead>
                <tr style={{ background: "#1a1f36" }}>
                  {ROLE_MATRIX.columns.map((col, i) => (
                    <th
                      key={col}
                      style={{
                        padding: "10px 14px",
                        fontSize: 12,
                        fontWeight: 700,
                        color: i === 0 ? "#e5e7eb" : "#ffcc33",
                        textAlign: i === 0 ? "left" : "center",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ROLE_MATRIX.rows.map((row, idx) => {
                  if (row.category) {
                    return (
                      <tr key={`cat-${idx}`}>
                        <td
                          colSpan={ROLE_MATRIX.columns.length}
                          style={{
                            padding: "10px 14px",
                            fontSize: 12,
                            fontWeight: 800,
                            color: "#1a1f36",
                            background: "#f1f5f9",
                            textTransform: "uppercase",
                            letterSpacing: "0.05em",
                            borderBottom: "1px solid #e5e7eb",
                          }}
                        >
                          {row.category}
                        </td>
                      </tr>
                    );
                  }
                  return (
                    <tr key={idx} style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}>
                      {row.map((cell, ci) => (
                        <td
                          key={ci}
                          style={cellStyle(
                            ci === 0,
                            cell === "Yes",
                            cell === "No"
                          )}
                        >
                          {ci === 0 ? cell : cell === "Yes" ? "\u2713 Yes" : cell === "No" ? "\u2717 No" : cell}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={{
            marginTop: 16, padding: 14, background: "#fffbeb", border: "1px solid #fde68a",
            borderRadius: 8, fontSize: 13, color: "#92400e", lineHeight: 1.5,
          }}>
            <strong>Note:</strong> All three roles are active. Admins see all brands and have full access.
            Analysts and Viewers are restricted to their assigned brands and module access flags (LLM/ADS).
            Monthly cost limits are enforced as hard stops per user.
          </div>
        </section>
      )}

      {/* Prompts reference */}
      {docSection === "prompts" && (
        <section>
          <h2 style={{ fontSize: 18, fontWeight: 700, color: "#1a1f36", marginBottom: 6 }}>
            AI Prompts Reference
          </h2>
          <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 16, lineHeight: 1.5 }}>
            All AI prompts used in the platform, their purpose, and editability.
          </p>

          <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 10 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#1a1f36" }}>
                  {["Prompt", "Settings Key", "Purpose", "Editable in UI", "Where"].map((col, i) => (
                    <th key={col} style={{
                      padding: "10px 14px", fontSize: 12, fontWeight: 700, color: i === 0 ? "#e5e7eb" : "#ffcc33",
                      textAlign: "left", textTransform: "uppercase", letterSpacing: "0.05em",
                    }}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  { cat: "LLM Discovery (MAD Pilot)" },
                  ["System Enforced Rules", "(hardcoded)", "Data layer rules, no-hallucination, output completeness", "No (read-only view)", "Settings > Copilot"],
                  ["Analyst Role Prompt", "copilot.analyst_prompt", "10-section analysis framework, table formats, GEO focus", "Yes (global + brand)", "Settings > Copilot"],
                  ["Initial Auto-Message", "copilot.initial_prompt", "First message sent when session starts", "Yes (global + brand)", "Settings > Copilot"],
                  ["Extraction Prompt", "(hardcoded)", "MapReduce per-question extraction schema + entity rules", "No", "-"],
                  ["Session Summary Prompt", "(hardcoded)", "Updates running memory after each response", "No", "-"],
                  { cat: "ADS Discovery (MAD Pilot)" },
                  ["Individual Ad Analysis", "ads.prompt.individual_analysis", "Per-ad extraction during first message", "Yes (global + brand)", "ADS Settings"],
                  ["Chat System Prompt", "ads.prompt.chat_system", "MAD Pilot system prompt for ADS module", "Yes (global + brand)", "ADS Settings"],
                  ["Auto-Analysis Message", "ads.prompt.chat_auto_message", "Starting message for auto-analysis", "Yes (global + brand)", "ADS Settings"],
                  { cat: "Models & Cost" },
                  ["ADS Individual Model", "ads.model.individual_analysis", "Model used for per-ad extraction", "Yes (global + brand)", "ADS Settings"],
                  ["ADS Bulk Model", "ads.model.bulk_analysis", "Model used for bulk/synthesis analysis", "Yes (global + brand)", "ADS Settings"],
                  ["Cost Alert Threshold", "ads.chat.cost_alert_threshold", "USD threshold for cost warning popup", "Yes (global + brand)", "ADS Settings"],
                ].map((row, idx) => {
                  if (row.cat) {
                    return (
                      <tr key={`cat-${idx}`}>
                        <td colSpan={5} style={{
                          padding: "10px 14px", fontSize: 12, fontWeight: 800, color: "#1a1f36",
                          background: "#f1f5f9", textTransform: "uppercase", letterSpacing: "0.05em",
                          borderBottom: "1px solid #e5e7eb",
                        }}>{row.cat}</td>
                      </tr>
                    );
                  }
                  return (
                    <tr key={idx} style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}>
                      {row.map((cell, ci) => (
                        <td key={ci} style={{
                          padding: "8px 14px", fontSize: 13,
                          fontWeight: ci === 0 ? 600 : 400,
                          color: ci === 0 ? "#1a1f36" : cell === "No" || cell === "No (read-only view)" ? "#dc2626" : "#374151",
                          borderBottom: "1px solid #f1f5f9",
                        }}>{cell}</td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Architecture overview */}
      {docSection === "architecture" && <ArchitectureDoc />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function AdminPage({ currentUser }) {
  const navigate = useNavigate();
  const userLabel = currentUser?.full_name || currentUser?.email || "User";

  const [toast, setToast] = useState(null);
  const showToast = (msg, type = "success") => setToast({ msg, type });
  const [adminTab, setAdminTab] = useState("management");

  // Users
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);

  // System info
  const [sysInfo, setSysInfo] = useState(null);
  const [sysLoading, setSysLoading] = useState(true);

  // Durable-job queue health (ARQ + Redis)
  const [queueHealth, setQueueHealth] = useState(null);

  // SearchAPI usage
  const [searchApiUsage, setSearchApiUsage] = useState(null);

  // Invitations
  const [invitations, setInvitations] = useState([]);
  const [invitationsLoading, setInvitationsLoading] = useState(true);

  // Brands (for user-brand assignment)
  const [allBrands, setAllBrands] = useState([]);

  // Copilot metrics
  const [metrics, setMetrics] = useState([]);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricTypes, setMetricTypes] = useState(null);

  // Domain categories
  const [domainCats, setDomainCats] = useState([]);
  const [domainCatsLoading, setDomainCatsLoading] = useState(true);
  const [domainSearch, setDomainSearch] = useState("");
  const [domainFilterCat, setDomainFilterCat] = useState("");

  // Release calendar
  const [releases, setReleases] = useState([]);
  const [releasesLoading, setReleasesLoading] = useState(true);
  const [showReleaseForm, setShowReleaseForm] = useState(false);
  const [editingRelease, setEditingRelease] = useState(null);
  const [releaseForm, setReleaseForm] = useState({ title: "", description: "", scheduled_date: "", environment: "staging", branch: "", send_demo_invite: false, demo_distribution_list: "" });
  const [domainFilterSrc, setDomainFilterSrc] = useState("");
  const [domainCategories, setDomainCategories] = useState([]);
  const [addDomainForm, setAddDomainForm] = useState({ domain: "", category: "brand_site" });

  const loadDomainCats = useCallback(async () => {
    setDomainCatsLoading(true);
    try {
      const data = await adminListDomainCategories(domainSearch, domainFilterCat, domainFilterSrc);
      setDomainCats(data.items || []);
      setDomainCategories(data.categories || []);
    } catch (e) {
      showToast("Failed to load domain categories", "error");
    } finally {
      setDomainCatsLoading(false);
    }
  }, [domainSearch, domainFilterCat, domainFilterSrc]);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const data = await adminListUsers();
      setUsers(data);
    } catch (e) {
      showToast("Failed to load users", "error");
    } finally {
      setUsersLoading(false);
    }
  }, []);

  const loadSystemInfo = useCallback(async () => {
    setSysLoading(true);
    try {
      const [info, usage, queue] = await Promise.all([
        adminGetSystemInfo(),
        fetchSearchApiUsage().catch(() => null),
        getQueueHealth().catch((err) => ({
          redis: "down",
          redis_error: err?.message || "request failed",
          worker_status: "missing",
        })),
      ]);
      setSysInfo(info);
      setSearchApiUsage(usage);
      setQueueHealth(queue);
    } catch (e) {
      showToast("Failed to load system info", "error");
    } finally {
      setSysLoading(false);
    }
  }, []);

  // Refresh queue health every 30s while the Admin page is open so the
  // badge reflects current Redis/worker state without a manual reload.
  useEffect(() => {
    const id = setInterval(() => {
      getQueueHealth()
        .then(setQueueHealth)
        .catch(() => {
          setQueueHealth({
            redis: "down",
            redis_error: "request failed",
            worker_status: "missing",
          });
        });
    }, 30000);
    return () => clearInterval(id);
  }, []);

  const BUILTIN_METRICS = [
    {
      id: "__builtin_brand_mention",
      name: "Brand Mentions",
      metric_type: "brand_mention",
      config_json: { brand_name: "(auto: audited brand)", aliases: [], include_domains: true },
      is_active: true,
      is_builtin: true,
      scope: "global",
      scope_id: null,
      created_at: null,
    },
    {
      id: "__builtin_url_domain",
      name: "URL/Domain Inventory",
      metric_type: "url_domain_count",
      config_json: { per_model: true },
      is_active: true,
      is_builtin: true,
      scope: "global",
      scope_id: null,
      created_at: null,
    },
    {
      id: "__builtin_source_categories",
      name: "Source Categories",
      metric_type: "source_categories",
      config_json: { per_model: true, method: "heuristic domain matching" },
      is_active: true,
      is_builtin: true,
      scope: "global",
      scope_id: null,
      created_at: null,
    },
    {
      id: "__builtin_summary",
      name: "Audit Summary Stats",
      metric_type: "summary",
      config_json: { includes: "question count, model count, errors, stale, per-model breakdown" },
      is_active: true,
      is_builtin: true,
      scope: "global",
      scope_id: null,
      created_at: null,
    },
    {
      id: "__builtin_question_index",
      name: "Question Index",
      metric_type: "index",
      config_json: { maps: "internal IDs → Q1, Q2, Q3..." },
      is_active: true,
      is_builtin: true,
      scope: "global",
      scope_id: null,
      created_at: null,
    },
    {
      id: "__builtin_competitors",
      name: "Top Mental Competitors",
      metric_type: "llm_extraction",
      config_json: { source: "MapReduce reduce phase", detects: "non-audited brands per model" },
      is_active: true,
      is_builtin: true,
      scope: "global",
      scope_id: null,
      created_at: null,
    },
    {
      id: "__builtin_sentiment",
      name: "Sentiment Toward Brand",
      metric_type: "llm_extraction",
      config_json: { source: "MapReduce reduce phase", values: "positive / neutral / negative / not_mentioned" },
      is_active: true,
      is_builtin: true,
      scope: "global",
      scope_id: null,
      created_at: null,
    },
    {
      id: "__builtin_consensus",
      name: "Consensus Distribution",
      metric_type: "llm_extraction",
      config_json: { source: "MapReduce reduce phase", values: "HIGH / MEDIUM / LOW per question" },
      is_active: true,
      is_builtin: true,
      scope: "global",
      scope_id: null,
      created_at: null,
    },
    {
      id: "__builtin_competitor_totals",
      name: "Total Competitor Mentions per Model",
      metric_type: "llm_extraction",
      config_json: { source: "MapReduce reduce phase", aggregates: "competitor counts per LLM" },
      is_active: true,
      is_builtin: true,
      scope: "global",
      scope_id: null,
      created_at: null,
    },
  ];

  const loadInvitations = useCallback(async () => {
    setInvitationsLoading(true);
    try {
      const data = await listInvitations();
      setInvitations(data);
    } catch (e) {
      // Silently fail if invitations table doesn't exist yet
    } finally {
      setInvitationsLoading(false);
    }
  }, []);

  const loadMetrics = useCallback(async () => {
    setMetricsLoading(true);
    try {
      const [data, types] = await Promise.all([
        copilotMetricList(),
        copilotMetricTypes().catch(() => null),
      ]);
      setMetrics([...BUILTIN_METRICS, ...data]);
      if (types) setMetricTypes(types.types);
    } catch (e) {
      showToast("Failed to load metrics", "error");
    } finally {
      setMetricsLoading(false);
    }
  }, []);

  const loadReleases = useCallback(async () => {
    try { setReleases(await listReleases()); }
    catch { /* table may not exist yet */ }
    finally { setReleasesLoading(false); }
  }, []);

  useEffect(() => {
    loadUsers();
    loadSystemInfo();
    loadMetrics();
    loadInvitations();
    loadDomainCats();
    loadReleases();
    fetchBrandHub().then((data) => setAllBrands(data?.items || data || [])).catch(() => {});
  }, [loadUsers, loadSystemInfo, loadMetrics, loadInvitations, loadDomainCats, loadReleases]);

  const handleCreateUser = async (data) => {
    await adminCreateUser(data);
    showToast(`User ${data.email} created`);
    await loadUsers();
  };

  const handleUpdateUser = async (userId, data) => {
    try {
      await adminUpdateUser(userId, data);
      showToast("User updated");
      await loadUsers();
    } catch (e) {
      showToast(e?.message || "Update failed", "error");
    }
  };

  const handleResetPassword = async (userId, newPassword) => {
    try {
      await adminUpdateUser(userId, { new_password: newPassword });
      showToast("Password reset");
    } catch (e) {
      showToast(e?.message || "Reset failed", "error");
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await adminDeleteUser(userId);
      setUsers((u) => u.filter((x) => x.id !== userId));
      showToast("User deleted");
    } catch (e) {
      showToast(e?.message || "Delete failed", "error");
    }
  };

  const thStyle = {
    padding: "8px 12px",
    textAlign: "left",
    fontSize: 12,
    fontWeight: 600,
    color: "#6b7280",
    borderBottom: "1px solid var(--border)",
    whiteSpace: "nowrap",
  };

  return (
    <AppShell
      headerSubtitle={`Admin Management • ${userLabel}`}
      headerRight={
        <UserActionsBar
          mode="module"
          currentUser={currentUser}
          onBackToModules={() => navigate("/modules")}
        />
      }
    >
      <header className="page-header">
        <div>
          <h1 className="page-title">Admin Management</h1>
          <p className="page-sub">
            User management, system configuration, and integrations.
          </p>
        </div>
      </header>

      {/* Tab navigation - segmented pill control */}
      <div
        style={{
          display: "inline-flex",
          gap: 4,
          padding: 5,
          background: "#f3f4f6",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          marginBottom: 24,
          boxShadow: "inset 0 1px 2px rgba(0,0,0,0.04)",
        }}
      >
        {[
          { id: "management", label: "Management", icon: "⚙︎" },
          { id: "report-layout", label: "Report Layout", icon: "▤" },
          { id: "releases", label: "Release Calendar", icon: "◫" },
          { id: "docs", label: "Documentation", icon: "□" },
        ].map((t) => {
          const active = adminTab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setAdminTab(t.id)}
              onMouseEnter={(e) => {
                if (!active) e.currentTarget.style.background = "#ffffff";
              }}
              onMouseLeave={(e) => {
                if (!active) e.currentTarget.style.background = "transparent";
              }}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 16px",
                fontSize: 13,
                fontWeight: active ? 700 : 500,
                color: active ? "#1a1f36" : "#6b7280",
                background: active ? "#ffffff" : "transparent",
                border: active ? "1px solid #e5e7eb" : "1px solid transparent",
                borderRadius: 8,
                cursor: "pointer",
                transition: "all 0.18s ease",
                boxShadow: active
                  ? "0 1px 3px rgba(26,31,54,0.10), 0 1px 2px rgba(26,31,54,0.06)"
                  : "none",
                letterSpacing: 0.1,
              }}
            >
              <span
                aria-hidden
                style={{
                  fontSize: 13,
                  opacity: active ? 1 : 0.6,
                  color: active ? "#1a1f36" : "#9ca3af",
                }}
              >
                {t.icon}
              </span>
              {t.label}
            </button>
          );
        })}
      </div>

      {adminTab === "management" && (<>
      {/* ---------------------------------------------------------------- */}
      {/* 1. User Management                                                */}
      {/* ---------------------------------------------------------------- */}
      <section style={{ marginTop: 20 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>
            Users ({users.length})
          </h2>
          <CreateUserForm onCreated={handleCreateUser} brands={allBrands} />
        </div>

        {usersLoading ? (
          <div style={{ color: "#9ca3af", fontSize: 13 }}>Loading...</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                background: "#fff",
                border: "1px solid var(--border)",
                borderRadius: 10,
              }}
            >
              <thead>
                <tr>
                  <th style={thStyle}>ID</th>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Name</th>
                  <th style={{ ...thStyle, textAlign: "center" }}>Role</th>
                  <th style={{ ...thStyle, textAlign: "center" }}>Modules</th>
                  <th style={{ ...thStyle, textAlign: "center" }}>Status</th>
                  <th style={thStyle}>Created</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <UserRow
                    key={u.id}
                    user={u}
                    currentUserId={currentUser?.id}
                    onSave={handleUpdateUser}
                    onDelete={handleDeleteUser}
                    onResetPw={handleResetPassword}
                    brands={allBrands}
                    onReset2fa={async (userId) => {
                      try {
                        const { tfa2faAdminReset } = await import("../services/api");
                        const res = await tfa2faAdminReset(userId);
                        showToast(res.message || "2FA reset");
                        await loadUsers();
                      } catch (e) {
                        showToast(e?.message || "Reset failed", "error");
                      }
                    }}
                  />
                ))}
                {users.length === 0 && (
                  <tr>
                    <td
                      colSpan={7}
                      style={{
                        padding: "20px 12px",
                        textAlign: "center",
                        color: "#9ca3af",
                        fontSize: 13,
                      }}
                    >
                      No users found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* 1b. Invitations                                                    */}
      {/* ---------------------------------------------------------------- */}
      <InvitationsSection
        invitations={invitations}
        loading={invitationsLoading}
        onInvite={async (email, isAdmin) => {
          try {
            const res = await inviteUser(email, isAdmin);
            showToast(res.message || `Invitation sent to ${email}`);
            await loadInvitations();
          } catch (e) {
            showToast(e?.message || "Failed to send invitation", "error");
          }
        }}
        onRevoke={async (id) => {
          try {
            await revokeInvitation(id);
            showToast("Invitation revoked");
            await loadInvitations();
          } catch (e) {
            showToast(e?.message || "Revoke failed", "error");
          }
        }}
        onResend={async (id) => {
          try {
            const res = await resendInvitation(id);
            showToast(res.email_sent ? "Invitation resent" : "Resent (email delivery uncertain)");
            await loadInvitations();
          } catch (e) {
            showToast(e?.message || "Resend failed", "error");
          }
        }}
        thStyle={thStyle}
      />

      {/* ---------------------------------------------------------------- */}
      {/* 2. System Configuration                                           */}
      {/* ---------------------------------------------------------------- */}
      <section style={{ marginTop: 36 }}>
        <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>
          System Configuration
        </h2>
        {sysLoading ? (
          <div style={{ color: "#9ca3af", fontSize: 13 }}>Loading...</div>
        ) : sysInfo ? (
          <>
            <div
              style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16 }}
            >
              <InfoCard
                label="Environment"
                value={sysInfo.env?.toUpperCase()}
                color={sysInfo.env === "prod" ? "#dc2626" : "#059669"}
              />
              <InfoCard
                label="Copilot Model"
                value={sysInfo.copilot_model}
              />
              <InfoCard
                label="Token Expiry"
                value={`${sysInfo.token_expiry_minutes} min`}
              />
              <InfoCard
                label="Database"
                value={sysInfo.database_url}
              />
            </div>

            <h3
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "#374151",
                marginBottom: 8,
              }}
            >
              Job Queue (ARQ + Redis)
            </h3>
            <QueueHealthCards queue={queueHealth} />

            <h3
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "#374151",
                marginTop: 16,
                marginBottom: 8,
              }}
            >
              API Keys
            </h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 16 }}>
              {Object.entries(sysInfo.api_keys || {}).map(([k, v]) => (
                <InfoCard
                  key={k}
                  label={k.replace(/_/g, " ").toUpperCase()}
                  value={v === true ? "Configured" : v === false ? "Not set" : (v || "Not set")}
                  color={v === true ? "#16a34a" : v === false ? "#ef4444" : v ? "#111827" : "#ef4444"}
                />
              ))}
            </div>

            <h3
              style={{
                fontSize: 13,
                fontWeight: 600,
                color: "#374151",
                marginBottom: 8,
              }}
            >
              SearchAPI Defaults
            </h3>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
              {Object.entries(sysInfo.searchapi_defaults || {}).map(([k, v]) => (
                <InfoCard
                  key={k}
                  label={k.replace(/_/g, " ").toUpperCase()}
                  value={v || "-"}
                />
              ))}
            </div>
          </>
        ) : null}
      </section>

      {/* ---------------------------------------------------------------- */}
      {/* 3. SearchAPI Usage                                                */}
      {/* ---------------------------------------------------------------- */}
      {searchApiUsage && !searchApiUsage.error && (
        <section style={{ marginTop: 36, marginBottom: 40 }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, marginBottom: 12 }}>
            SearchAPI.io Usage
          </h2>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {searchApiUsage.plan_name && (
              <InfoCard label="Plan" value={searchApiUsage.plan_name} />
            )}
            {searchApiUsage.total_searches != null && (
              <InfoCard
                label="Total Searches Used"
                value={searchApiUsage.total_searches?.toLocaleString()}
              />
            )}
            {searchApiUsage.remaining_searches != null && (
              <InfoCard
                label="Remaining Searches"
                value={searchApiUsage.remaining_searches?.toLocaleString()}
                color={searchApiUsage.remaining_searches < 100 ? "#ef4444" : "#059669"}
              />
            )}
          </div>
        </section>
      )}

      {/* ---------------------------------------------------------------- */}
      {/* 4. Copilot Custom Metrics                                         */}
      {/* ---------------------------------------------------------------- */}
      <section style={{ marginTop: 36, marginBottom: 40 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <h2 style={{ fontSize: 15, fontWeight: 600, margin: 0 }}>
            Copilot Metrics ({metrics.filter(m => !m.is_builtin).length} custom, {metrics.filter(m => m.is_builtin).length} built-in)
          </h2>
          <MetricCreateForm
            metricTypes={metricTypes}
            onCreated={async (data) => {
              await copilotMetricCreate(data);
              showToast(`Metric "${data.name}" created`);
              await loadMetrics();
            }}
          />
        </div>
        <MetricsHelpTooltip />

        {metricsLoading ? (
          <div style={{ color: "#9ca3af", fontSize: 13 }}>Loading...</div>
        ) : (
          <div style={{ display: "grid", gap: 10 }}>
            {metrics.map((m) => (
              <MetricCard
                key={m.id}
                metric={m}
                onToggle={async () => {
                  await copilotMetricUpdate(m.id, { is_active: !m.is_active });
                  await loadMetrics();
                }}
                onDelete={async () => {
                  await copilotMetricDelete(m.id);
                  showToast(`Metric "${m.name}" deleted`);
                  await loadMetrics();
                }}
              />
            ))}
            {metrics.length === 0 && (
              <div style={{ padding: 20, textAlign: "center", color: "#9ca3af", fontSize: 13, background: "#f9fafb", borderRadius: 10, border: "1px solid var(--border)" }}>
                No metrics configured.
              </div>
            )}
          </div>
        )}
      </section>

      {/* Domain Categories removed - now in Citations Module at /citations */}
      {false && <section style={{ marginTop: 36 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: "#1a1f36", marginBottom: 12 }}>
          Domain Categories (Legacy)
          <span style={{ fontSize: 12, fontWeight: 400, color: "#6b7280", marginLeft: 8 }}>
            Self-growing classification cache ({domainCats.length} domains)
          </span>
        </h2>

        {/* Filters + Add */}
        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <input
            style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "6px 10px", fontSize: 13, width: 200 }}
            placeholder="Search domain..."
            value={domainSearch}
            onChange={(e) => setDomainSearch(e.target.value)}
          />
          <select
            style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "6px 10px", fontSize: 13 }}
            value={domainFilterCat}
            onChange={(e) => setDomainFilterCat(e.target.value)}
          >
            <option value="">All categories</option>
            {domainCategories.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select
            style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "6px 10px", fontSize: 13 }}
            value={domainFilterSrc}
            onChange={(e) => setDomainFilterSrc(e.target.value)}
          >
            <option value="">All sources</option>
            <option value="admin">admin</option>
            <option value="llm">llm</option>
            <option value="auto">auto</option>
          </select>
          <button className="btn-secondary" style={{ padding: "6px 12px", fontSize: 13 }} onClick={loadDomainCats}>
            Filter
          </button>
        </div>

        {/* Add domain form */}
        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "flex-end", background: "#f9fafb", padding: 12, borderRadius: 8, border: "1px solid #e5e7eb" }}>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: "#374151", display: "block" }}>Domain</label>
            <input
              style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "5px 10px", fontSize: 13, width: 200 }}
              placeholder="example.com"
              value={addDomainForm.domain}
              onChange={(e) => setAddDomainForm((f) => ({ ...f, domain: e.target.value }))}
            />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 600, color: "#374151", display: "block" }}>Category</label>
            <select
              style={{ border: "1px solid #d1d5db", borderRadius: 6, padding: "5px 10px", fontSize: 13 }}
              value={addDomainForm.category}
              onChange={(e) => setAddDomainForm((f) => ({ ...f, category: e.target.value }))}
            >
              {domainCategories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <button
            className="btn-primary"
            style={{ padding: "6px 14px", fontSize: 13 }}
            onClick={async () => {
              if (!addDomainForm.domain.trim()) return;
              try {
                await adminCreateDomainCategory(addDomainForm.domain, addDomainForm.category);
                setAddDomainForm({ domain: "", category: "brand_site" });
                showToast("Domain added");
                loadDomainCats();
              } catch (e) {
                showToast(e?.message || "Failed to add domain", "error");
              }
            }}
          >
            + Add
          </button>
        </div>

        {/* Table */}
        {domainCatsLoading ? (
          <p style={{ color: "#9ca3af", fontSize: 13 }}>Loading...</p>
        ) : domainCats.length === 0 ? (
          <p style={{ color: "#9ca3af", fontSize: 13, textAlign: "center", padding: 20 }}>No domains cached yet. They will appear after running audits.</p>
        ) : (
          <div style={{ overflowX: "auto", border: "1px solid var(--border)", borderRadius: 10 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "#374151", borderBottom: "2px solid var(--border)" }}>Domain</th>
                  <th style={{ padding: "8px 12px", textAlign: "left", fontSize: 12, fontWeight: 700, color: "#374151", borderBottom: "2px solid var(--border)" }}>Category</th>
                  <th style={{ padding: "8px 12px", textAlign: "center", fontSize: 12, fontWeight: 700, color: "#374151", borderBottom: "2px solid var(--border)" }}>Source</th>
                  <th style={{ padding: "8px 12px", textAlign: "center", fontSize: 12, fontWeight: 700, color: "#374151", borderBottom: "2px solid var(--border)" }}>Audits</th>
                  <th style={{ padding: "8px 12px", textAlign: "right", fontSize: 12, fontWeight: 700, color: "#374151", borderBottom: "2px solid var(--border)" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {domainCats.map((d) => (
                  <tr key={d.id} style={{ borderTop: "1px solid var(--border)" }}>
                    <td style={{ padding: "6px 12px", fontSize: 13, fontWeight: 500 }}>{d.domain}</td>
                    <td style={{ padding: "6px 12px" }}>
                      <select
                        style={{ border: "1px solid #d1d5db", borderRadius: 5, padding: "2px 6px", fontSize: 12, background: d.source === "admin" ? "#fef3c7" : "#fff" }}
                        value={d.category}
                        onChange={async (e) => {
                          try {
                            await adminUpdateDomainCategory(d.id, e.target.value);
                            showToast(`${d.domain} → ${e.target.value}`);
                            loadDomainCats();
                          } catch (err) {
                            showToast("Failed to update", "error");
                          }
                        }}
                      >
                        {domainCategories.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: "6px 12px", textAlign: "center" }}>
                      <span style={{
                        fontSize: 10, fontWeight: 600, padding: "2px 6px", borderRadius: 4,
                        background: d.source === "admin" ? "#fef3c7" : d.source === "llm" ? "#ede9fe" : "#f3f4f6",
                        color: d.source === "admin" ? "#92400e" : d.source === "llm" ? "#6d28d9" : "#6b7280",
                      }}>
                        {d.source}
                      </span>
                    </td>
                    <td style={{ padding: "6px 12px", textAlign: "center", fontSize: 12, color: "#6b7280" }}>
                      {d.audit_count || 0}
                    </td>
                    <td style={{ padding: "6px 12px", textAlign: "right" }}>
                      <button
                        style={{ background: "none", border: "none", color: "#ef4444", fontSize: 12, cursor: "pointer", textDecoration: "underline" }}
                        onClick={async () => {
                          if (!window.confirm(`Delete classification for ${d.domain}? It will be re-classified on next encounter.`)) return;
                          try {
                            await adminDeleteDomainCategory(d.id);
                            showToast(`${d.domain} removed`);
                            loadDomainCats();
                          } catch (err) {
                            showToast("Failed to delete", "error");
                          }
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>}
      </>)}

      {adminTab === "report-layout" && (
        <ReportLayoutSection showToast={showToast} />
      )}

      {adminTab === "releases" && (
        <section>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700 }}>Release Calendar</h2>
            <button
              onClick={() => {
                setEditingRelease(null);
                setReleaseForm({ title: "", description: "", scheduled_date: "", environment: "staging", branch: "", send_demo_invite: false, demo_distribution_list: "" });
                setShowReleaseForm(true);
              }}
              style={{ padding: "8px 16px", background: "#1a1f36", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
            >
              + New Release
            </button>
          </div>

          {showReleaseForm && (
            <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 10, padding: 16, marginBottom: 16 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>Title *</label>
                  <input value={releaseForm.title} onChange={(e) => setReleaseForm(f => ({ ...f, title: e.target.value }))}
                    style={{ width: "100%", padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13 }} placeholder="e.g., v2.4 - Copilot improvements" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>Scheduled Date *</label>
                  <input type="datetime-local" value={releaseForm.scheduled_date} onChange={(e) => setReleaseForm(f => ({ ...f, scheduled_date: e.target.value }))}
                    style={{ width: "100%", padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13 }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>Environment</label>
                  <select value={releaseForm.environment} onChange={(e) => setReleaseForm(f => ({ ...f, environment: e.target.value }))}
                    style={{ width: "100%", padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13 }}>
                    <option value="staging">Staging</option>
                    <option value="production">Production</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>Branch</label>
                  <input value={releaseForm.branch} onChange={(e) => setReleaseForm(f => ({ ...f, branch: e.target.value }))}
                    style={{ width: "100%", padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13 }} placeholder="e.g., staging or main" />
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: "#374151", display: "block", marginBottom: 4 }}>Description</label>
                <textarea value={releaseForm.description} onChange={(e) => setReleaseForm(f => ({ ...f, description: e.target.value }))}
                  rows={3} style={{ width: "100%", padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, resize: "vertical" }}
                  placeholder="What's included in this release..." />
              </div>
              <div style={{ marginBottom: 12, padding: "12px", background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 8 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", marginBottom: 8 }}>
                  <input type="checkbox" checked={releaseForm.send_demo_invite || false}
                    onChange={(e) => setReleaseForm(f => ({ ...f, send_demo_invite: e.target.checked }))} />
                  Send demo calendar invite
                </label>
                {releaseForm.send_demo_invite && (
                  <div>
                    {editingRelease?.invite_recipient && (
                      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 6 }}>
                        Already invited: {editingRelease.invite_recipient.split(",").map((e, i) => (
                          <span key={i} style={{ display: "inline-block", background: "#e0f2fe", color: "#0369a1", padding: "1px 6px", borderRadius: 4, fontSize: 11, margin: "2px 3px 2px 0" }}>{e.trim()}</span>
                        ))}
                      </div>
                    )}
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#374151", marginBottom: 4 }}>Select from app users:</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 8 }}>
                      {users.filter(u => u.is_active !== false).map((u) => {
                        const email = u.email;
                        const currentEmails = (releaseForm.demo_distribution_list || "").split(",").map(e => e.trim()).filter(Boolean);
                        const alreadyInvited = (editingRelease?.invite_recipient || "").split(",").map(e => e.trim());
                        const isSelected = currentEmails.includes(email);
                        const wasInvited = alreadyInvited.includes(email);
                        return (
                          <button
                            key={u.id}
                            type="button"
                            onClick={() => {
                              if (wasInvited) return;
                              setReleaseForm(f => {
                                const emails = (f.demo_distribution_list || "").split(",").map(e => e.trim()).filter(Boolean);
                                const updated = isSelected ? emails.filter(e => e !== email) : [...emails, email];
                                return { ...f, demo_distribution_list: updated.join(", ") };
                              });
                            }}
                            style={{
                              padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, cursor: wasInvited ? "default" : "pointer",
                              border: "1px solid",
                              background: wasInvited ? "#f3f4f6" : isSelected ? "#1a1f36" : "#fff",
                              color: wasInvited ? "#9ca3af" : isSelected ? "#fff" : "#374151",
                              borderColor: wasInvited ? "#e5e7eb" : isSelected ? "#1a1f36" : "#d1d5db",
                              opacity: wasInvited ? 0.6 : 1,
                              transition: "all 0.15s",
                            }}
                            title={wasInvited ? "Already invited" : isSelected ? "Click to remove" : "Click to add"}
                          >
                            {u.full_name || email}{wasInvited ? " (sent)" : isSelected ? " ✓" : ""}
                          </button>
                        );
                      })}
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#374151", marginBottom: 4 }}>Or type external emails:</div>
                    <input value={releaseForm.demo_distribution_list || ""} onChange={(e) => setReleaseForm(f => ({ ...f, demo_distribution_list: e.target.value }))}
                      style={{ width: "100%", padding: "6px 10px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13 }}
                      placeholder={editingRelease?.invite_recipient ? "Add more emails, comma-separated" : "Email addresses, comma-separated (e.g., team@madaboutdigital.net)"} />
                    <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 4 }}>Click user names above or type emails manually. Separate multiple with commas.</div>
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  onClick={async () => {
                    try {
                      if (editingRelease) {
                        await updateRelease(editingRelease.id, releaseForm);
                        showToast("Release updated");
                      } else {
                        await createRelease(releaseForm);
                        showToast("Release created");
                      }
                      setShowReleaseForm(false);
                      await loadReleases();
                    } catch (e) { showToast(e?.message || "Failed", "error"); }
                  }}
                  disabled={!releaseForm.title || !releaseForm.scheduled_date}
                  style={{ padding: "8px 20px", background: "#1a1f36", color: "#fff", border: "none", borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: (!releaseForm.title || !releaseForm.scheduled_date) ? 0.5 : 1 }}
                >
                  {editingRelease ? "Update" : "Create"}
                </button>
                <button onClick={() => setShowReleaseForm(false)}
                  style={{ padding: "8px 20px", background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, cursor: "pointer" }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {releasesLoading ? (
            <p style={{ color: "#9ca3af", fontSize: 13 }}>Loading...</p>
          ) : releases.length === 0 ? (
            <p style={{ color: "#9ca3af", fontSize: 13 }}>No releases scheduled. Click "+ New Release" to plan one.</p>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff", border: "1px solid var(--border)", borderRadius: 10 }}>
              <thead>
                <tr style={{ background: "#f9fafb" }}>
                  {["Date", "Title", "Env", "Status", "Branch", "Invitees", "Created By", "Actions"].map((h) => (
                    <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 12, fontWeight: 600, color: "#6b7280", borderBottom: "1px solid #e5e7eb" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {releases.map((r) => {
                  const envColor = r.environment === "production" ? { bg: "#fef2f2", color: "#dc2626" } : { bg: "#fffbeb", color: "#d97706" };
                  const statusColor = r.status === "successful" ? { bg: "#f0fdf4", color: "#16a34a" }
                    : r.status === "deployed" ? { bg: "#eff6ff", color: "#2563eb" }
                    : r.status === "cancelled" ? { bg: "#f9fafb", color: "#9ca3af" }
                    : { bg: "#fffbeb", color: "#d97706" };
                  return (
                    <tr key={r.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                      <td style={{ padding: "10px 12px", fontSize: 13 }}>
                        {r.scheduled_date ? new Date(r.scheduled_date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-"}
                      </td>
                      <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 600 }}>
                        {r.title}
                        {r.description && <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 400, marginTop: 2 }}>{r.description.slice(0, 80)}{r.description.length > 80 ? "..." : ""}</div>}
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: envColor.bg, color: envColor.color, textTransform: "uppercase" }}>
                          {r.environment === "production" ? "PROD" : "STG"}
                        </span>
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: statusColor.bg, color: statusColor.color, textTransform: "uppercase" }}>
                          {r.status}
                        </span>
                      </td>
                      <td style={{ padding: "10px 12px", fontSize: 12, color: "#6b7280", fontFamily: "monospace" }}>{r.branch || "-"}</td>
                      <td style={{ padding: "10px 12px", fontSize: 12 }}>
                        {r.invite_recipient ? r.invite_recipient.split(",").map((e, i) => (
                          <span key={i} style={{ display: "inline-block", background: "#e0f2fe", color: "#0369a1", padding: "1px 6px", borderRadius: 4, fontSize: 10, margin: "1px 2px" }}>{e.trim()}</span>
                        )) : <span style={{ color: "#9ca3af" }}>-</span>}
                      </td>
                      <td style={{ padding: "10px 12px", fontSize: 12, color: "#6b7280" }}>{r.created_by_name || "-"}</td>
                      <td style={{ padding: "10px 12px" }}>
                        <div style={{ display: "flex", gap: 6 }}>
                          {r.status === "planned" && (
                            <button onClick={async () => {
                              try { await updateRelease(r.id, { status: "deployed" }); showToast("Marked as deployed"); await loadReleases(); }
                              catch (e) { showToast(e?.message || "Failed", "error"); }
                            }} style={{ fontSize: 11, padding: "4px 10px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontWeight: 600 }}>
                              Deployed
                            </button>
                          )}
                          {r.status === "deployed" && (
                            <button onClick={async () => {
                              try { await updateRelease(r.id, { status: "successful" }); showToast("Marked as successful - version updated"); await loadReleases(); }
                              catch (e) { showToast(e?.message || "Failed", "error"); }
                            }} style={{ fontSize: 11, padding: "4px 10px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontWeight: 600 }}>
                              Successful
                            </button>
                          )}
                          <button onClick={() => {
                            setEditingRelease(r);
                            setReleaseForm({ title: r.title, description: r.description || "", scheduled_date: r.scheduled_date ? r.scheduled_date.slice(0, 16) : "", environment: r.environment, branch: r.branch || "", send_demo_invite: false, demo_distribution_list: "" });
                            setShowReleaseForm(true);
                          }} style={{ fontSize: 11, padding: "4px 10px", background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db", borderRadius: 4, cursor: "pointer" }}>
                            Edit
                          </button>
                          {r.status !== "successful" && (
                            <button onClick={async () => {
                              if (!window.confirm("Delete this release?")) return;
                              try { await deleteRelease(r.id); showToast("Deleted"); await loadReleases(); }
                              catch (e) { showToast(e?.message || "Failed", "error"); }
                            }} style={{ fontSize: 11, padding: "4px 10px", background: "#fff", color: "#dc2626", border: "1px solid #fecaca", borderRadius: 4, cursor: "pointer" }}>
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>
      )}

      {adminTab === "docs" && (
        <DocumentationTab />
      )}

      {toast && (
        <Toast
          message={toast.msg}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </AppShell>
  );
}
